import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { GlowOrb } from "@/components/common/GlowOrb";
import {
  LogOut, Sparkles, FileText, Users, Mail, Newspaper, Briefcase, FolderKanban,
  UserSquare2, RefreshCw, Trash2, Loader2, Plus, Save, X, Download, ExternalLink, UploadCloud, Star, Layers, Menu, Eye, EyeOff
} from "lucide-react";
import { COMPANY, SERVICES, PRODUCTS } from "@/data/site";
import {
  useTableTools, TableToolbar, SelectAllCell, SelectRowCell, bulkDeleteIds,
} from "@/components/admin/TableTools";

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [news, setNews] = useState([]);
  const [clientsData, setClientsData] = useState([]);
  const [recognitions, setRecognitions] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [viewingConversation, setViewingConversation] = useState(null);
  const [serviceMedia, setServiceMedia] = useState({});   // { [slug]: video_url }
  const [productMedia, setProductMedia] = useState({});   // { [slug]: video_url }
  const [pageMedia, setPageMedia] = useState({});         // { [key]: video_url } — Partnership, About, etc.
  const [editingBlogCover, setEditingBlogCover] = useState(null);
  const [applications, setApplications] = useState([]);
  const [gen, setGen] = useState({ topic: "", category: "AI", target_country: "United Kingdom", length: "medium", target_keywords: "" });
  const [genLoading, setGenLoading] = useState(false);

  const [editingProject, setEditingProject] = useState(null); // null | {} for new, or existing project
  const [editingJob, setEditingJob] = useState(null);
  const [editingNews, setEditingNews] = useState(null);
  const [editingClient, setEditingClient] = useState(null);
  const [editingRecognition, setEditingRecognition] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [editingReview, setEditingReview] = useState(null);
  const [showcaseVideos, setShowcaseVideos] = useState([]);
  const [editingShowcaseVideo, setEditingShowcaseVideo] = useState(null);
  const [portfolioCategories, setPortfolioCategories] = useState([]);
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [editingPortfolioCategory, setEditingPortfolioCategory] = useState(null);
  const [editingPortfolioItem, setEditingPortfolioItem] = useState(null);
  const [heroWords, setHeroWords] = useState([]);
  const [editingHeroWord, setEditingHeroWord] = useState(null);
  const [navItems, setNavItems] = useState([]);
  const [editingNavItem, setEditingNavItem] = useState(null);
  const [managingSubmenusOf, setManagingSubmenusOf] = useState(null); // parent nav item
  const [estimates, setEstimates] = useState([]);
  const [viewingEstimate, setViewingEstimate] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [training, setTraining] = useState({ system_prompt: "", knowledge_text: "" });
  const [trainingBusy, setTrainingBusy] = useState(false);
  const trainingFileRef = useRef(null);

  useEffect(() => {
    if (!localStorage.getItem("cl_token")) { nav("/admin/login"); return; }
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      const [s, q, b, c, p, j, a, n, cl, rc, cv, sm, pm, pgm, rv, sv, pfc, pfi, hw, ni, est] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/quotations"),
        api.get("/admin/blogs"),
        api.get("/contact"),
        api.get("/admin/projects"),
        api.get("/admin/jobs"),
        api.get("/admin/applications"),
        api.get("/admin/news"),
        api.get("/admin/clients"),
        api.get("/admin/recognitions"),
        api.get("/admin/ai-agent/conversations"),
        api.get("/services/media"),
        api.get("/products/media"),
        api.get("/page-media"),
        api.get("/admin/reviews"),
        api.get("/admin/showcase-videos"),
        api.get("/admin/portfolio-categories"),
        api.get("/admin/portfolio"),
        api.get("/admin/hero-words"),
        api.get("/admin/nav-items"),
        api.get("/admin/estimates"),
      ]);
      setStats(s.data); setQuotes(q.data); setBlogs(b.data); setContacts(c.data);
      setProjects(p.data); setJobs(j.data); setApplications(a.data); setNews(n.data);
      setClientsData(cl.data);
      setRecognitions(rc.data);
      setConversations(cv.data);
      setReviews(rv.data);
      setShowcaseVideos(sv.data);
      setPortfolioCategories(pfc.data);
      setPortfolioItems(pfi.data);
      setHeroWords(hw.data);
      setNavItems(ni.data);
      setEstimates(est.data);
      // Convert array into slug->url map for quick lookup
      const mediaMap = {};
      (sm.data || []).forEach((m) => { mediaMap[m.slug] = m.video_url || ""; });
      setServiceMedia(mediaMap);
      const productMediaMap = {};
      (pm.data || []).forEach((m) => { productMediaMap[m.slug] = m.video_url || ""; });
      setProductMedia(productMediaMap);
      const pageMediaMap = {};
      (pgm.data || []).forEach((m) => { pageMediaMap[m.key] = m.video_url || ""; });
      setPageMedia(pageMediaMap);
      try {
        const t = await api.get("/admin/ai-agent/training");
        setTraining({ system_prompt: t.data.system_prompt || "", knowledge_text: t.data.knowledge_text || "" });
      } catch { /* first time — ignore */ }
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        localStorage.removeItem("cl_token");
        nav("/admin/login");
      }
    }
  };

  const logout = () => {
    localStorage.removeItem("cl_token");
    localStorage.removeItem("cl_email");
    nav("/admin/login");
  };

  const updateStatus = async (id, status) => {
    await api.patch(`/quotations/${id}`, { status });
    toast.success("Updated"); loadAll();
  };

  const generateBlog = async (e) => {
    e.preventDefault();
    if (!gen.topic.trim()) return;
    setGenLoading(true);
    try {
      const kws = gen.target_keywords.split(",").map(s => s.trim()).filter(Boolean);
      await api.post("/admin/blogs/generate", { ...gen, target_keywords: kws, auto_publish: true });
      toast.success("Article generated & published.");
      setGen({ ...gen, topic: "", target_keywords: "" });
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generation failed.");
    } finally { setGenLoading(false); }
  };

  const deleteBlog = async (id) => {
    if (!confirm("Delete this article?")) return;
    await api.delete(`/admin/blogs/${id}`); toast.success("Deleted"); loadAll();
  };
  const togglePublish = async (b) => {
    await api.patch(`/admin/blogs/${b.id}`, { published: !b.published }); loadAll();
  };

  // ---- Projects CRUD ----
  const emptyProject = {
    name: "", client: "", industry: "AI", country: "United Kingdom",
    cover_image: "", thumbnail_image: "", tagline: "",
    problem: "", approach: "", tech_stack: [], results: [],
    order: 100, published: true,
    // ---- Phase 4.0.1 ----
    publish_as_case_study: false,
    publication_status: "DRAFT",
    slug_override: "",
    services: [], industries: [], markets: [],
    short_description: "", hero_subtitle: "",
    challenge: "", solution: "",
    solution_type: "", platform: "", external_url: "",
    technologies: [], capabilities: [], workflow: [],
    cloud_lab_role: [],
    metrics: [], testimonials: [],
    seo: {},
    answer_profile: {},
    hero_image: "", gallery: [],
    related_projects: [],
  };
  const saveProject = async (payload) => {
    try {
      if (payload.id) {
        await api.patch(`/admin/projects/${payload.id}`, payload);
        toast.success("Project updated.");
      } else {
        await api.post("/admin/projects", payload);
        toast.success("Project created.");
      }
      setEditingProject(null); loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed."); }
  };
  const deleteProject = async (id) => {
    if (!confirm("Delete this project?")) return;
    await api.delete(`/admin/projects/${id}`); toast.success("Deleted"); loadAll();
  };
  // Quick toggle used by the eye icon in the Projects table — flips the
  // `published` flag without opening the editor. Full ProjectIn payload is
  // re-sent because the PATCH endpoint validates the whole model.
  const toggleProjectPublished = async (p) => {
    try {
      await api.patch(`/admin/projects/${p.id}`, {
        name: p.name,
        client: p.client || null,
        industry: p.industry,
        country: p.country,
        cover_image: p.cover_image,
        thumbnail_image: p.thumbnail_image || null,
        tagline: p.tagline,
        problem: p.problem || "",
        approach: p.approach || "",
        tech_stack: p.tech_stack || [],
        results: p.results || [],
        order: p.order || 100,
        published: !p.published,
      });
      toast.success(p.published ? "Project hidden" : "Project is now live");
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Toggle failed.");
    }
  };

  // ---- Jobs CRUD ----
  const emptyJob = { title: "", department: "Engineering", location: "", type: "Full-time", remote: true, summary: "", description: "", requirements: [], benefits: [], published: true };
  const saveJob = async (payload) => {
    try {
      if (payload.id) {
        await api.patch(`/admin/jobs/${payload.id}`, payload);
        toast.success("Job updated.");
      } else {
        await api.post("/admin/jobs", payload);
        toast.success("Job created.");
      }
      setEditingJob(null); loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed."); }
  };
  const deleteJob = async (id) => {
    if (!confirm("Delete this job?")) return;
    await api.delete(`/admin/jobs/${id}`); toast.success("Deleted"); loadAll();
  };

  // ---- News CRUD ----
  const emptyNews = { title: "", description: "", image: "", link: "", tag: "Launch", order: 100, published: true };
  const saveNews = async (payload) => {
    try {
      if (payload.id) {
        await api.patch(`/admin/news/${payload.id}`, payload);
        toast.success("News updated.");
      } else {
        await api.post("/admin/news", payload);
        toast.success("News created.");
      }
      setEditingNews(null); loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed."); }
  };
  const deleteNews = async (id) => {
    if (!confirm("Delete this news item?")) return;
    await api.delete(`/admin/news/${id}`); toast.success("Deleted"); loadAll();
  };

  // ---- Clients CRUD ----
  const emptyClient = { name: "", logo_url: "", domain: "", website: "", order: 100, published: true };
  const saveClient = async (payload) => {
    try {
      if (payload.id) {
        await api.patch(`/admin/clients/${payload.id}`, payload);
        toast.success("Client updated.");
      } else {
        await api.post("/admin/clients", payload);
        toast.success("Client added.");
      }
      setEditingClient(null); loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed."); }
  };
  const deleteClient = async (id) => {
    if (!confirm("Delete this client?")) return;
    await api.delete(`/admin/clients/${id}`); toast.success("Deleted"); loadAll();
  };

  // ---- Recognitions ----
  const saveRecognition = async (payload) => {
    try {
      if (payload.id) {
        await api.patch(`/admin/recognitions/${payload.id}`, payload);
        toast.success("Recognition updated.");
      } else {
        await api.post("/admin/recognitions", payload);
        toast.success("Recognition added.");
      }
      setEditingRecognition(null); loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed."); }
  };
  const deleteRecognition = async (id) => {
    if (!confirm("Delete this recognition?")) return;
    await api.delete(`/admin/recognitions/${id}`); toast.success("Deleted"); loadAll();
  };

  // ---- Reviews ----
  const emptyReview = { author_name: "", author_role: "", avatar_url: "", rating: 5, text: "", source: "Google", source_url: "", country: "", order: 100, published: true };
  const saveReview = async (payload) => {
    try {
      if (payload.id) {
        const { id, ...body } = payload;
        await api.patch(`/admin/reviews/${id}`, body);
        toast.success("Review updated.");
      } else {
        await api.post("/admin/reviews", payload);
        toast.success("Review added.");
      }
      setEditingReview(null); loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed."); }
  };
  const deleteReview = async (id) => {
    if (!confirm("Delete this review?")) return;
    await api.delete(`/admin/reviews/${id}`); toast.success("Deleted"); loadAll();
  };

  // ---- Showcase Videos ----
  const emptyShowcaseVideo = { title: "", subtitle: "", video_url: "", thumbnail_url: "", duration: "", tag: "", project_link: "", order: 100, published: true };
  const saveShowcaseVideo = async (payload) => {
    try {
      if (payload.id) {
        const { id, ...body } = payload;
        await api.patch(`/admin/showcase-videos/${id}`, body);
        toast.success("Video updated.");
      } else {
        await api.post("/admin/showcase-videos", payload);
        toast.success("Video added to playlist.");
      }
      setEditingShowcaseVideo(null); loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed."); }
  };
  const deleteShowcaseVideo = async (id) => {
    if (!confirm("Delete this video from the playlist?")) return;
    await api.delete(`/admin/showcase-videos/${id}`); toast.success("Deleted"); loadAll();
  };

  // ---- Portfolio Categories ----
  const emptyPortfolioCategory = { name: "", slug: "", order: 100, published: true };
  const savePortfolioCategory = async (payload) => {
    try {
      if (payload.id) {
        const { id, ...body } = payload;
        await api.patch(`/admin/portfolio-categories/${id}`, body);
        toast.success("Category updated.");
      } else {
        await api.post("/admin/portfolio-categories", payload);
        toast.success("Category added.");
      }
      setEditingPortfolioCategory(null); loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed."); }
  };
  const deletePortfolioCategory = async (id) => {
    if (!confirm("Delete this category? Portfolio items assigned to it will still exist but appear uncategorised.")) return;
    await api.delete(`/admin/portfolio-categories/${id}`); toast.success("Deleted"); loadAll();
  };

  // ---- Portfolio Items ----
  const emptyPortfolioItem = { title: "", subtitle: "", slug: "", category_slug: (portfolioCategories[0]?.slug || ""), cover_image: "", video_url: "", media_format: "landscape", year: "", client: "", tech_stack: [], external_url: "", description: "", gallery: [], outcomes: [], challenge: "", solution: "", features: [], process: [], timeline: "", markets: [], services: [], industries: [], order: 100, published: true };
  const savePortfolioItem = async (payload) => {
    try {
      if (payload.id) {
        const { id, ...body } = payload;
        await api.patch(`/admin/portfolio/${id}`, body);
        toast.success("Portfolio item updated.");
      } else {
        await api.post("/admin/portfolio", payload);
        toast.success("Portfolio item added.");
      }
      setEditingPortfolioItem(null); loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed."); }
  };
  const deletePortfolioItem = async (id) => {
    if (!confirm("Delete this portfolio item?")) return;
    await api.delete(`/admin/portfolio/${id}`); toast.success("Deleted"); loadAll();
  };

  // ---- Hero Rotator ("We are the developers of ___") ----
  const emptyHeroWord = { word: "", tagline: "", order: 100, published: true };
  const saveHeroWord = async (payload) => {
    try {
      if (payload.id) {
        await api.patch(`/admin/hero-words/${payload.id}`, payload);
        toast.success("Rotator entry updated.");
      } else {
        await api.post("/admin/hero-words", payload);
        toast.success("Rotator entry added.");
      }
      // Purge the SWR cache so the public hero picks up the change immediately.
      try { sessionStorage.removeItem("swr:/hero-words"); } catch { /* ignore */ }
      setEditingHeroWord(null); loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed.");
    }
  };
  const deleteHeroWord = async (id) => {
    if (!confirm("Remove this word from the hero rotator?")) return;
    await api.delete(`/admin/hero-words/${id}`);
    try { sessionStorage.removeItem("swr:/hero-words"); } catch { /* ignore */ }
    toast.success("Removed"); loadAll();
  };
  const toggleHeroWordPublished = async (w) => {
    try {
      await api.patch(`/admin/hero-words/${w.id}`, {
        word: w.word,
        tagline: w.tagline || null,
        order: w.order || 100,
        published: !w.published,
      });
      try { sessionStorage.removeItem("swr:/hero-words"); } catch { /* ignore */ }
      toast.success(w.published ? "Word hidden from rotator" : "Word is now live");
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Toggle failed.");
    }
  };

  // ---- Header Navigation ----
  const emptyNavItem = { label: "", path: "", order: 100, published: true, parent_id: null, tagline: null };
  const purgeNavCache = () => { try { sessionStorage.removeItem("swr:/nav-items"); } catch { /* ignore */ } };
  const saveNavItem = async (payload) => {
    try {
      // Ensure optional fields are always present on the wire
      const body = {
        label: payload.label,
        path: payload.path,
        order: payload.order,
        published: payload.published,
        parent_id: payload.parent_id ?? null,
        tagline: payload.tagline ?? null,
      };
      if (payload.id) {
        await api.patch(`/admin/nav-items/${payload.id}`, body);
        toast.success("Nav item updated.");
      } else {
        await api.post("/admin/nav-items", body);
        toast.success("Nav item added.");
      }
      purgeNavCache(); setEditingNavItem(null); loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed.");
    }
  };
  const deleteNavItem = async (id) => {
    // If deleting a parent that still has children, warn.
    const children = navItems.filter((n) => n.parent_id === id);
    const msg = children.length
      ? `This item has ${children.length} submenu ${children.length === 1 ? "entry" : "entries"} — delete them all too?`
      : "Remove this item from the header nav?";
    if (!confirm(msg)) return;
    if (children.length) {
      await Promise.all(children.map((c) => api.delete(`/admin/nav-items/${c.id}`)));
    }
    await api.delete(`/admin/nav-items/${id}`); purgeNavCache();
    toast.success("Removed"); loadAll();
  };
  const toggleNavItemPublished = async (n) => {
    try {
      await api.patch(`/admin/nav-items/${n.id}`, {
        label: n.label,
        path: n.path,
        order: n.order || 100,
        published: !n.published,
        parent_id: n.parent_id ?? null,
        tagline: n.tagline ?? null,
      });
      purgeNavCache();
      toast.success(n.published ? "Nav item hidden" : "Nav item is now live");
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Toggle failed.");
    }
  };

  // ---- Applications ----
  const updateAppStatus = async (id, status) => {
    await api.patch(`/admin/applications/${id}`, { status }); toast.success("Updated"); loadAll();
  };
  const downloadCV = async (aid, filename) => {
    try {
      const res = await api.get(`/admin/applications/${aid}/cv`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = filename || "cv"; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not download CV."); }
  };
  const deleteApplication = async (aid) => {
    if (!confirm("Delete this application (and its CV) permanently?")) return;
    await api.delete(`/admin/applications/${aid}`);
    toast.success("Deleted"); loadAll();
  };

  // ---- Reusable table tools (search + multi-select) for every list tab ----
  const tt = {
    projects: useTableTools(projects, ["name", "industry", "country", "client", "slug"]),
    jobs: useTableTools(jobs, ["title", "department", "location", "type", "slug"]),
    applications: useTableTools(applications, ["name", "email", "phone", "job_title", "status"]),
    news: useTableTools(news, ["title", "description", "tag"]),
    clients: useTableTools(clientsData, ["name", "domain", "website"]),
    recognitions: useTableTools(recognitions, ["title", "description", "link"]),
    reviews: useTableTools(reviews, ["author_name", "author_role", "country", "source", "text"]),
    showcaseVideos: useTableTools(showcaseVideos, ["title", "subtitle", "tag", "video_url"]),
    portfolio: useTableTools(portfolioItems, ["title", "subtitle", "category_slug", "client", "year"]),
    portfolioCats: useTableTools(portfolioCategories, ["name", "slug"]),
    heroWords: useTableTools(heroWords, ["word", "tagline"]),
    navItems: useTableTools(navItems.filter((n) => !n.parent_id), ["label", "path"]),
    estimates: useTableTools(estimates, ["name", "email", "company", "country", "project_type", "budget_range", "status"]),
    conversations: useTableTools(conversations, ["name", "email", "phone", "question", "channel"]),
    quotations: useTableTools(quotes, ["name", "email", "company", "service", "budget", "status"]),
    blogs: useTableTools(blogs, ["title", "category", "slug"]),
    contacts: useTableTools(contacts, ["name", "email", "subject", "message"]),
  };

  // Generic bulk delete helper — pass the endpoint path & the tt entry key
  const bulkDelete = async (ttKey, endpoint, label = "items") => {
    const ids = tt[ttKey].selectedIds;
    if (!ids.size) return;
    if (!confirm(`Delete ${ids.size} ${label} permanently?`)) return;
    const n = await bulkDeleteIds(ids, (id) => api.delete(`${endpoint}/${id}`));
    toast.success(`Deleted ${n} ${label}.`);
    tt[ttKey].clear();
    loadAll();
  };

  const TABS = [
    { id: "dashboard", label: "Overview", icon: FileText },
    { id: "hero-rotator", label: "Hero Rotator", icon: Sparkles },
    { id: "nav-items", label: "Header Nav", icon: Menu },
    { id: "projects", label: "Projects", icon: FolderKanban },
    { id: "jobs", label: "Jobs", icon: Briefcase },
    { id: "applications", label: "Applications", icon: UserSquare2 },
    { id: "news", label: "What's Latest", icon: Sparkles },
    { id: "clients", label: "Clients", icon: Users },
    { id: "recognitions", label: "Recognitions", icon: Sparkles },
    { id: "reviews", label: "Reviews", icon: Star },
    { id: "showcase-videos", label: "Video Showcase", icon: FolderKanban },
    { id: "portfolio", label: "Portfolio", icon: FolderKanban },
    { id: "portfolio-cats", label: "Portfolio Categories", icon: Layers },
    { id: "service-videos", label: "Service Videos", icon: FolderKanban },
    { id: "product-videos", label: "Product Videos", icon: FolderKanban },
    { id: "page-videos", label: "Page Videos", icon: FolderKanban },
    { id: "ai-agent", label: "AI Agent", icon: Sparkles },
    { id: "conversations", label: "AI Conversations", icon: Mail },
    { id: "quotations", label: "Quotations", icon: Users },
    { id: "estimates", label: "Estimate Requests", icon: Sparkles },
    { id: "blogs", label: "Blog Engine", icon: Newspaper },
    { id: "contacts", label: "Messages", icon: Mail },
  ];

  return (
    <div className="relative min-h-screen bg-background" data-testid="admin-dashboard-page">
      <GlowOrb className="top-0 left-0" color="primary" size={400} />

      {/* Sidebar — desktop always visible, mobile slide-in drawer */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 lg:w-72 border-r border-border bg-card/95 backdrop-blur-xl flex flex-col transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="admin-sidebar"
      >
        <div className="p-5 border-b border-border flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <img src={COMPANY.logo} alt="CloudLab" className="h-8 w-auto" />
            <div className="min-w-0">
              <div className="font-heading font-bold text-sm leading-tight">CloudLab</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Admin console</div>
            </div>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden p-1.5 rounded-lg hover:bg-foreground/10" aria-label="Close menu">
            <X className="w-4 h-4" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto p-3 no-scrollbar" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => { setTab(t.id); setSidebarOpen(false); }}
              data-testid={`admin-tab-${t.id}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm mb-1 transition-colors duration-200 ${
                tab === t.id
                  ? "bg-primary text-primary-foreground shadow-[0_10px_30px_-10px_hsl(var(--primary)/0.5)]"
                  : "text-foreground/70 hover:text-foreground hover:bg-foreground/5"
              }`}
            >
              <t.icon className="w-4 h-4 shrink-0" />
              <span className="truncate">{t.label}</span>
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-border">
          <div className="text-[11px] text-muted-foreground truncate mb-2 font-mono">
            {localStorage.getItem("cl_email") || "admin"}
          </div>
          <button onClick={logout} className="w-full rounded-xl px-3 py-2 text-xs inline-flex items-center gap-2 hover:bg-destructive/10 text-destructive transition-colors" data-testid="admin-logout">
            <LogOut className="w-3.5 h-3.5" /> Logout
          </button>
        </div>
      </aside>

      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
          data-testid="admin-sidebar-backdrop"
        />
      )}

      {/* Main content */}
      <div className="lg:pl-72 min-h-screen">
        <header className="sticky top-0 z-20 border-b border-border bg-background/80 backdrop-blur-xl">
          <div className="flex items-center justify-between px-4 md:px-8 py-3">
            <div className="flex items-center gap-3 min-w-0">
              <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 rounded-lg hover:bg-foreground/10" aria-label="Open menu" data-testid="admin-menu-toggle">
                <Menu className="w-5 h-5" />
              </button>
              <div className="font-heading font-semibold text-sm md:text-base truncate">
                {TABS.find((t) => t.id === tab)?.label || "Dashboard"}
              </div>
            </div>
            <button onClick={loadAll} className="p-2 rounded-lg hover:bg-foreground/10" data-testid="admin-refresh" aria-label="Refresh data">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 relative">

        {tab === "dashboard" && stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="admin-stats">
            <StatCard label="Projects" value={stats.projects} accent="primary" />
            <StatCard label="Open jobs" value={stats.jobs} accent="primary" />
            <StatCard label="New applications" value={stats.applications_new} accent="secondary" />
            <StatCard label="Total applications" value={stats.applications} />
            <StatCard label="Quotations" value={stats.quotations} />
            <StatCard label="New quotes" value={stats.quotations_new} accent="secondary" />
            <StatCard label="Contacts" value={stats.contacts} />
            <StatCard label="Subscribers" value={stats.subscribers} />
            <StatCard label="Articles" value={stats.blogs} />
            <StatCard label="Published" value={stats.blogs_published} accent="primary" />
          </div>
        )}

        {tab === "hero-rotator" && (
          <div className="space-y-4" data-testid="admin-hero-rotator">
            <div className="flex justify-between items-center gap-3 flex-wrap">
              <div>
                <h2 className="font-heading font-bold text-2xl">Hero Rotator</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  The animated product names that follow &ldquo;<span className="italic">We are the developers of</span>&rdquo; on the landing-page hero. Add / edit / hide entries — the rotator on the live site updates on next visit.
                </p>
              </div>
              <button onClick={() => setEditingHeroWord(emptyHeroWord)} className="rounded-full px-4 py-2 bg-primary text-primary-foreground text-sm inline-flex items-center gap-2" data-testid="admin-hero-word-new">
                <Plus className="w-4 h-4" /> Add entry
              </button>
            </div>
            <TableToolbar
              search={tt.heroWords.search}
              onSearchChange={tt.heroWords.setSearch}
              placeholder="Search rotator entries…"
              selectedCount={tt.heroWords.selectedIds.size}
              onBulkDelete={() => bulkDelete("heroWords", "/admin/hero-words", "rotator entries")}
              onClearSelection={tt.heroWords.clear}
              testidPrefix="hero-words"
            />
            <div className="glass rounded-3xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="text-left text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground bg-foreground/5">
                  <tr>
                    <SelectAllCell items={tt.heroWords.filtered} selectedIds={tt.heroWords.selectedIds} onToggleAll={tt.heroWords.toggleAll} testid="hero-words-select-all" />
                    <th className="p-4">Word</th>
                    <th className="p-4">Tagline</th>
                    <th className="p-4">Order</th>
                    <th className="p-4">Status</th>
                    <th className="p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {tt.heroWords.filtered.length === 0 && (
                    <tr><td colSpan={6} className="p-10 text-center text-muted-foreground">{heroWords.length === 0 ? "No rotator entries yet." : "No matches."}</td></tr>
                  )}
                  {tt.heroWords.filtered.map((w) => (
                    <tr key={w.id} className="border-t border-border hover:bg-foreground/5" data-testid={`admin-hero-word-row-${w.id}`}>
                      <SelectRowCell id={w.id} selectedIds={tt.heroWords.selectedIds} onToggle={tt.heroWords.toggle} testid={`hero-words-select-${w.id}`} />
                      <td className="p-4 font-medium">{w.word}</td>
                      <td className="p-4 text-xs text-muted-foreground line-clamp-2 max-w-[420px]">{w.tagline || "—"}</td>
                      <td className="p-4">{w.order}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-mono ${w.published ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                          {w.published ? "Live" : "Hidden"}
                        </span>
                      </td>
                      <td className="p-4 flex gap-2">
                        <button
                          onClick={() => toggleHeroWordPublished(w)}
                          className={`p-2 rounded hover:bg-foreground/10 ${w.published ? "" : "text-muted-foreground"}`}
                          title={w.published ? "Hide from rotator" : "Show in rotator"}
                          data-testid={`admin-hero-word-toggle-${w.id}`}
                        >
                          {w.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </button>
                        <button onClick={() => setEditingHeroWord(w)} className="p-2 rounded hover:bg-foreground/10" data-testid={`admin-hero-word-edit-${w.id}`}>Edit</button>
                        <button onClick={() => deleteHeroWord(w.id)} className="p-2 rounded hover:bg-destructive/10 text-destructive"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "nav-items" && (
          <div className="space-y-4" data-testid="admin-nav-items">
            <div className="flex justify-between items-center gap-3 flex-wrap">
              <div>
                <h2 className="font-heading font-bold text-2xl">Header Navigation</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Every top-level link in the site header. Use <span className="italic">Submenus</span> to add / edit / hide the dropdown children for that item. Cache is purged on save so changes appear on the very next visit.
                </p>
              </div>
              <button onClick={() => setEditingNavItem({ ...emptyNavItem })} className="rounded-full px-4 py-2 bg-primary text-primary-foreground text-sm inline-flex items-center gap-2" data-testid="admin-nav-item-new">
                <Plus className="w-4 h-4" /> Add top-level link
              </button>
            </div>
            <TableToolbar
              search={tt.navItems.search}
              onSearchChange={tt.navItems.setSearch}
              placeholder="Search nav items…"
              selectedCount={tt.navItems.selectedIds.size}
              onBulkDelete={() => { bulkDelete("navItems", "/admin/nav-items", "nav items"); purgeNavCache(); }}
              onClearSelection={tt.navItems.clear}
              testidPrefix="nav-items"
            />
            <div className="glass rounded-3xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="text-left text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground bg-foreground/5">
                  <tr>
                    <SelectAllCell items={tt.navItems.filtered} selectedIds={tt.navItems.selectedIds} onToggleAll={tt.navItems.toggleAll} testid="nav-items-select-all" />
                    <th className="p-4">Label</th>
                    <th className="p-4">Path / URL</th>
                    <th className="p-4">Submenus</th>
                    <th className="p-4">Order</th>
                    <th className="p-4">Status</th>
                    <th className="p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {tt.navItems.filtered.length === 0 && (
                    <tr><td colSpan={7} className="p-10 text-center text-muted-foreground">{navItems.filter((n) => !n.parent_id).length === 0 ? "No nav items yet." : "No matches."}</td></tr>
                  )}
                  {tt.navItems.filtered.map((n) => {
                    const childCount = navItems.filter((x) => x.parent_id === n.id).length;
                    return (
                      <tr key={n.id} className="border-t border-border hover:bg-foreground/5" data-testid={`admin-nav-item-row-${n.id}`}>
                        <SelectRowCell id={n.id} selectedIds={tt.navItems.selectedIds} onToggle={tt.navItems.toggle} testid={`nav-items-select-${n.id}`} />
                        <td className="p-4 font-medium">{n.label}</td>
                        <td className="p-4 text-xs font-mono text-muted-foreground break-all">{n.path}</td>
                        <td className="p-4">
                          <button
                            onClick={() => setManagingSubmenusOf(n)}
                            className="text-xs rounded-full border border-border px-3 py-1 hover:bg-foreground/10 inline-flex items-center gap-1"
                            data-testid={`admin-nav-item-submenus-${n.id}`}
                          >
                            <Layers className="w-3.5 h-3.5" /> {childCount} · Manage
                          </button>
                        </td>
                        <td className="p-4">{n.order}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-mono ${n.published ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                            {n.published ? "Live" : "Hidden"}
                          </span>
                        </td>
                        <td className="p-4 flex gap-2">
                          <button
                            onClick={() => toggleNavItemPublished(n)}
                            className={`p-2 rounded hover:bg-foreground/10 ${n.published ? "" : "text-muted-foreground"}`}
                            title={n.published ? "Hide from header" : "Show in header"}
                            data-testid={`admin-nav-item-toggle-${n.id}`}
                          >
                            {n.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                          </button>
                          <button onClick={() => setEditingNavItem(n)} className="p-2 rounded hover:bg-foreground/10" data-testid={`admin-nav-item-edit-${n.id}`}>Edit</button>
                          <button onClick={() => deleteNavItem(n.id)} className="p-2 rounded hover:bg-destructive/10 text-destructive"><Trash2 className="w-4 h-4" /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "projects" && (
          <div className="space-y-4" data-testid="admin-projects">
            <div className="flex justify-between items-center gap-3 flex-wrap">
              <h2 className="font-heading font-bold text-2xl">Featured Projects</h2>
              <button onClick={() => setEditingProject(emptyProject)} className="rounded-full px-4 py-2 bg-primary text-primary-foreground text-sm inline-flex items-center gap-2" data-testid="admin-project-new"><Plus className="w-4 h-4" /> New project</button>
            </div>
            <TableToolbar
              search={tt.projects.search}
              onSearchChange={tt.projects.setSearch}
              placeholder="Search projects by name, industry, country…"
              selectedCount={tt.projects.selectedIds.size}
              onBulkDelete={() => bulkDelete("projects", "/admin/projects", "projects")}
              onClearSelection={tt.projects.clear}
              testidPrefix="projects"
            />
            <div className="glass rounded-3xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="text-left text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground bg-foreground/5">
                  <tr>
                    <SelectAllCell items={tt.projects.filtered} selectedIds={tt.projects.selectedIds} onToggleAll={tt.projects.toggleAll} testid="projects-select-all" />
                    <th className="p-4">Name</th><th className="p-4">Industry</th><th className="p-4">Country</th><th className="p-4">Order</th><th className="p-4">Status</th><th className="p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {tt.projects.filtered.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-muted-foreground">{projects.length === 0 ? "No projects yet." : "No matches."}</td></tr>}
                  {tt.projects.filtered.map((p) => (
                    <tr key={p.id} className="border-t border-border hover:bg-foreground/5" data-testid={`admin-project-row-${p.slug}`}>
                      <SelectRowCell id={p.id} selectedIds={tt.projects.selectedIds} onToggle={tt.projects.toggle} testid={`projects-select-${p.slug}`} />
                      <td className="p-4">
                        <div className="font-medium">{p.name}</div>
                        <div className="text-xs text-muted-foreground">/{p.slug}</div>
                      </td>
                      <td className="p-4">{p.industry}</td>
                      <td className="p-4">{p.country}</td>
                      <td className="p-4">{p.order}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-mono ${p.published ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>{p.published ? "Live" : "Draft"}</span>
                      </td>
                      <td className="p-4 flex gap-2">
                        <a href={`/projects/${p.slug}`} target="_blank" rel="noreferrer" className="p-2 rounded hover:bg-foreground/10"><ExternalLink className="w-4 h-4" /></a>
                        <button
                          onClick={() => toggleProjectPublished(p)}
                          className={`p-2 rounded hover:bg-foreground/10 ${p.published ? "" : "text-muted-foreground"}`}
                          title={p.published ? "Hide from public site" : "Publish to public site"}
                          data-testid={`admin-project-toggle-${p.slug}`}
                        >
                          {p.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </button>
                        <button onClick={() => setEditingProject(p)} className="p-2 rounded hover:bg-foreground/10" data-testid={`admin-project-edit-${p.slug}`}>Edit</button>
                        <button onClick={() => deleteProject(p.id)} className="p-2 rounded hover:bg-destructive/10 text-destructive"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "jobs" && (
          <div className="space-y-4" data-testid="admin-jobs">
            <div className="flex justify-between items-center gap-3 flex-wrap">
              <h2 className="font-heading font-bold text-2xl">Open Jobs</h2>
              <button onClick={() => setEditingJob(emptyJob)} className="rounded-full px-4 py-2 bg-primary text-primary-foreground text-sm inline-flex items-center gap-2" data-testid="admin-job-new"><Plus className="w-4 h-4" /> New job</button>
            </div>
            <TableToolbar
              search={tt.jobs.search}
              onSearchChange={tt.jobs.setSearch}
              placeholder="Search jobs by title, department, location…"
              selectedCount={tt.jobs.selectedIds.size}
              onBulkDelete={() => bulkDelete("jobs", "/admin/jobs", "jobs")}
              onClearSelection={tt.jobs.clear}
              testidPrefix="jobs"
            />
            <div className="glass rounded-3xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="text-left text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground bg-foreground/5">
                  <tr>
                    <SelectAllCell items={tt.jobs.filtered} selectedIds={tt.jobs.selectedIds} onToggleAll={tt.jobs.toggleAll} testid="jobs-select-all" />
                    <th className="p-4">Title</th><th className="p-4">Department</th><th className="p-4">Location</th><th className="p-4">Type</th><th className="p-4">Status</th><th className="p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {tt.jobs.filtered.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-muted-foreground">{jobs.length === 0 ? "No jobs yet." : "No matches."}</td></tr>}
                  {tt.jobs.filtered.map((j) => (
                    <tr key={j.id} className="border-t border-border hover:bg-foreground/5" data-testid={`admin-job-row-${j.slug}`}>
                      <SelectRowCell id={j.id} selectedIds={tt.jobs.selectedIds} onToggle={tt.jobs.toggle} testid={`jobs-select-${j.slug}`} />
                      <td className="p-4">
                        <div className="font-medium">{j.title}</div>
                        <div className="text-xs text-muted-foreground">/{j.slug}</div>
                      </td>
                      <td className="p-4">{j.department}</td>
                      <td className="p-4">{j.location}{j.remote ? " · Remote" : ""}</td>
                      <td className="p-4">{j.type}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-mono ${j.published ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>{j.published ? "Live" : "Draft"}</span>
                      </td>
                      <td className="p-4 flex gap-2">
                        <a href={`/careers/${j.slug}`} target="_blank" rel="noreferrer" className="p-2 rounded hover:bg-foreground/10"><ExternalLink className="w-4 h-4" /></a>
                        <button onClick={() => setEditingJob(j)} className="p-2 rounded hover:bg-foreground/10" data-testid={`admin-job-edit-${j.slug}`}>Edit</button>
                        <button onClick={() => deleteJob(j.id)} className="p-2 rounded hover:bg-destructive/10 text-destructive"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "applications" && (
          <div className="space-y-4" data-testid="admin-applications">
            <div className="flex justify-between items-center gap-3 flex-wrap">
              <h2 className="font-heading font-bold text-2xl">Career Applications</h2>
            </div>
            <TableToolbar
              search={tt.applications.search}
              onSearchChange={tt.applications.setSearch}
              placeholder="Search applicants by name, email, role, status…"
              selectedCount={tt.applications.selectedIds.size}
              onBulkDelete={() => bulkDelete("applications", "/admin/applications", "applications")}
              onClearSelection={tt.applications.clear}
              testidPrefix="applications"
            />
            <div className="glass rounded-3xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="text-left text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground bg-foreground/5">
                  <tr>
                    <SelectAllCell items={tt.applications.filtered} selectedIds={tt.applications.selectedIds} onToggleAll={tt.applications.toggleAll} testid="applications-select-all" />
                    <th className="p-4">Candidate</th><th className="p-4">Role</th><th className="p-4">CV</th><th className="p-4">Status</th><th className="p-4">Received</th><th className="p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {tt.applications.filtered.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-muted-foreground">{applications.length === 0 ? "No applications yet." : "No matches."}</td></tr>}
                  {tt.applications.filtered.map((a) => (
                    <tr key={a.id} className="border-t border-border hover:bg-foreground/5" data-testid={`admin-app-row-${a.id}`}>
                      <SelectRowCell id={a.id} selectedIds={tt.applications.selectedIds} onToggle={tt.applications.toggle} testid={`applications-select-${a.id}`} />
                      <td className="p-4">
                        <div className="font-medium">{a.name}</div>
                        <div className="text-xs text-muted-foreground">{a.email}{a.phone ? ` · ${a.phone}` : ""}</div>
                        {a.cover_letter && <div className="mt-1 text-xs text-muted-foreground max-w-md line-clamp-2">{a.cover_letter}</div>}
                      </td>
                      <td className="p-4">{a.job_title}</td>
                      <td className="p-4">
                        <button onClick={() => downloadCV(a.id, a.cv_filename)} className="inline-flex items-center gap-1 text-xs hover:text-primary" data-testid={`admin-app-cv-${a.id}`}>
                          <Download className="w-3.5 h-3.5" /> {a.cv_filename}
                        </button>
                      </td>
                      <td className="p-4">
                        <select value={a.status} onChange={(e) => updateAppStatus(a.id, e.target.value)}
                          className="bg-transparent border border-border rounded-full px-3 py-1 text-xs" data-testid={`admin-app-status-${a.id}`}>
                          {["new","reviewing","interview","offer","hired","rejected"].map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="p-4 text-xs text-muted-foreground">{new Date(a.created_at).toLocaleString()}</td>
                      <td className="p-4">
                        <button onClick={() => deleteApplication(a.id)} className="p-2 rounded hover:bg-destructive/10 text-destructive" data-testid={`admin-app-delete-${a.id}`}><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "news" && (
          <div className="space-y-4" data-testid="admin-news">
            <div className="flex justify-between items-center gap-3 flex-wrap">
              <h2 className="font-heading font-bold text-2xl">What&apos;s Latest</h2>
              <button onClick={() => setEditingNews(emptyNews)} className="rounded-full px-4 py-2 bg-primary text-primary-foreground text-sm inline-flex items-center gap-2" data-testid="admin-news-new"><Plus className="w-4 h-4" /> New item</button>
            </div>
            <TableToolbar
              search={tt.news.search}
              onSearchChange={tt.news.setSearch}
              placeholder="Search news items by title, tag, description…"
              selectedCount={tt.news.selectedIds.size}
              onBulkDelete={() => bulkDelete("news", "/admin/news", "news items")}
              onClearSelection={tt.news.clear}
              testidPrefix="news"
            />
            <div className="glass rounded-3xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="text-left text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground bg-foreground/5">
                  <tr>
                    <SelectAllCell items={tt.news.filtered} selectedIds={tt.news.selectedIds} onToggleAll={tt.news.toggleAll} testid="news-select-all" />
                    <th className="p-4">Title</th><th className="p-4">Tag</th><th className="p-4">Order</th><th className="p-4">Status</th><th className="p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {tt.news.filtered.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-muted-foreground">{news.length === 0 ? "No news yet." : "No matches."}</td></tr>}
                  {tt.news.filtered.map((n) => (
                    <tr key={n.id} className="border-t border-border hover:bg-foreground/5" data-testid={`admin-news-row-${n.id}`}>
                      <SelectRowCell id={n.id} selectedIds={tt.news.selectedIds} onToggle={tt.news.toggle} testid={`news-select-${n.id}`} />
                      <td className="p-4">
                        <div className="font-medium">{n.title}</div>
                        <div className="text-xs text-muted-foreground line-clamp-1">{n.description}</div>
                      </td>
                      <td className="p-4">{n.tag || "—"}</td>
                      <td className="p-4">{n.order}</td>
                      <td className="p-4"><span className={`px-2 py-1 rounded-full text-xs font-mono ${n.published ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>{n.published ? "Live" : "Draft"}</span></td>
                      <td className="p-4 flex gap-2">
                        <button onClick={() => setEditingNews(n)} className="p-2 rounded hover:bg-foreground/10">Edit</button>
                        <button onClick={() => deleteNews(n.id)} className="p-2 rounded hover:bg-destructive/10 text-destructive"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "clients" && (
          <div className="space-y-4" data-testid="admin-clients">
            <div className="flex justify-between items-center gap-3 flex-wrap">
              <h2 className="font-heading font-bold text-2xl">Clients</h2>
              <button onClick={() => setEditingClient(emptyClient)} className="rounded-full px-4 py-2 bg-primary text-primary-foreground text-sm inline-flex items-center gap-2" data-testid="admin-client-new"><Plus className="w-4 h-4" /> Add client</button>
            </div>
            <p className="text-sm text-muted-foreground">
              Upload a logo image URL (best), or set a brand <em>domain</em> (e.g. <code className="text-xs bg-muted px-1.5 py-0.5 rounded">pizzahut.com</code>) so Clearbit auto-fetches the logo. If neither is set, we render a monogram tile with the client&apos;s initials.
            </p>
            <TableToolbar
              search={tt.clients.search}
              onSearchChange={tt.clients.setSearch}
              placeholder="Search clients by name or domain…"
              selectedCount={tt.clients.selectedIds.size}
              onBulkDelete={() => bulkDelete("clients", "/admin/clients", "clients")}
              onClearSelection={tt.clients.clear}
              testidPrefix="clients"
            />
            <div className="glass rounded-3xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="text-left text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground bg-foreground/5">
                  <tr>
                    <SelectAllCell items={tt.clients.filtered} selectedIds={tt.clients.selectedIds} onToggleAll={tt.clients.toggleAll} testid="clients-select-all" />
                    <th className="p-4">Preview</th><th className="p-4">Name</th><th className="p-4">Source</th><th className="p-4">Order</th><th className="p-4">Status</th><th className="p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {tt.clients.filtered.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-muted-foreground">{clientsData.length === 0 ? "No clients yet." : "No matches."}</td></tr>}
                  {tt.clients.filtered.map((c) => {
                    const src = c.logo_url || (c.domain ? `https://logo.clearbit.com/${c.domain}?size=100` : null);
                    return (
                      <tr key={c.id} className="border-t border-border hover:bg-foreground/5" data-testid={`admin-client-row-${c.id}`}>
                        <SelectRowCell id={c.id} selectedIds={tt.clients.selectedIds} onToggle={tt.clients.toggle} testid={`clients-select-${c.id}`} />
                        <td className="p-4">
                          <div className="w-16 h-10 rounded border border-border bg-background flex items-center justify-center overflow-hidden">
                            {src ? <img src={src} alt={c.name} className="max-h-8 max-w-full object-contain" onError={(e) => e.currentTarget.style.display='none'} /> : <span className="text-[10px] font-mono uppercase text-muted-foreground">—</span>}
                          </div>
                        </td>
                        <td className="p-4 font-medium">{c.name}</td>
                        <td className="p-4 text-xs text-muted-foreground">
                          {c.logo_url ? "Uploaded URL" : c.domain ? `Clearbit / ${c.domain}` : "Monogram"}
                        </td>
                        <td className="p-4">{c.order}</td>
                        <td className="p-4"><span className={`px-2 py-1 rounded-full text-xs font-mono ${c.published ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>{c.published ? "Live" : "Draft"}</span></td>
                        <td className="p-4 flex gap-2">
                          <button onClick={() => setEditingClient(c)} className="p-2 rounded hover:bg-foreground/10">Edit</button>
                          <button onClick={() => deleteClient(c.id)} className="p-2 rounded hover:bg-destructive/10 text-destructive"><Trash2 className="w-4 h-4" /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "recognitions" && (
          <div className="space-y-4" data-testid="admin-recognitions">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="font-heading font-bold">Recognitions slider</div>
                <div className="text-xs text-muted-foreground mt-1">Award / press / partnership mentions shown just below the hero. Auto-rotates by the duration on each item.</div>
              </div>
              <button onClick={() => setEditingRecognition({ title: "", description: "", image: "", link: "", duration_ms: 5000, order: 100, published: true })} className="rounded-full px-4 py-2 bg-primary text-primary-foreground text-sm inline-flex items-center gap-2" data-testid="admin-recognition-new"><Plus className="w-4 h-4" /> New recognition</button>
            </div>
            <TableToolbar
              search={tt.recognitions.search}
              onSearchChange={tt.recognitions.setSearch}
              placeholder="Search recognitions…"
              selectedCount={tt.recognitions.selectedIds.size}
              onBulkDelete={() => bulkDelete("recognitions", "/admin/recognitions", "recognitions")}
              onClearSelection={tt.recognitions.clear}
              testidPrefix="recognitions"
            />
            <div className="glass rounded-3xl overflow-hidden" data-testid="admin-recognitions-table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground bg-foreground/5">
                    <tr>
                      <SelectAllCell items={tt.recognitions.filtered} selectedIds={tt.recognitions.selectedIds} onToggleAll={tt.recognitions.toggleAll} testid="recognitions-select-all" />
                      <th className="p-4">Title</th><th className="p-4">Duration</th><th className="p-4">Link</th><th className="p-4">Order</th><th className="p-4">Status</th><th className="p-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tt.recognitions.filtered.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-muted-foreground">{recognitions.length === 0 ? "No recognitions yet." : "No matches."}</td></tr>}
                    {tt.recognitions.filtered.map((r) => (
                      <tr key={r.id} className="border-t border-border hover:bg-foreground/5" data-testid={`admin-recognition-row-${r.id}`}>
                        <SelectRowCell id={r.id} selectedIds={tt.recognitions.selectedIds} onToggle={tt.recognitions.toggle} testid={`recognitions-select-${r.id}`} />
                        <td className="p-4"><div className="font-medium">{r.title}</div><div className="text-xs text-muted-foreground line-clamp-1">{r.description}</div></td>
                        <td className="p-4 text-xs font-mono">{(r.duration_ms / 1000).toFixed(1)}s</td>
                        <td className="p-4 text-xs text-muted-foreground line-clamp-1 max-w-[220px]">{r.link || "—"}</td>
                        <td className="p-4">{r.order}</td>
                        <td className="p-4"><span className={`px-2 py-1 rounded-full text-xs font-mono ${r.published ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>{r.published ? "Live" : "Draft"}</span></td>
                        <td className="p-4 flex gap-2">
                          <button onClick={() => setEditingRecognition(r)} className="p-2 rounded hover:bg-foreground/10">Edit</button>
                          <button onClick={() => deleteRecognition(r.id)} className="p-2 rounded hover:bg-destructive/10 text-destructive"><Trash2 className="w-4 h-4" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {tab === "reviews" && (
          <div className="space-y-4" data-testid="admin-reviews">
            <div className="flex justify-between items-center gap-3 flex-wrap">
              <div>
                <h2 className="font-heading font-bold text-2xl">Client reviews</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Curated reviews shown on the landing page in the auto-scrolling &ldquo;What clients say&rdquo; section (just after Articles).
                </p>
              </div>
              <button onClick={() => setEditingReview(emptyReview)} className="rounded-full px-4 py-2 bg-primary text-primary-foreground text-sm inline-flex items-center gap-2" data-testid="admin-review-new">
                <Plus className="w-4 h-4" /> Add review
              </button>
            </div>
            <TableToolbar
              search={tt.reviews.search}
              onSearchChange={tt.reviews.setSearch}
              placeholder="Search reviews by author, source, text…"
              selectedCount={tt.reviews.selectedIds.size}
              onBulkDelete={() => bulkDelete("reviews", "/admin/reviews", "reviews")}
              onClearSelection={tt.reviews.clear}
              testidPrefix="reviews"
            />
            <div className="glass rounded-3xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="text-left text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground bg-foreground/5">
                  <tr>
                    <SelectAllCell items={tt.reviews.filtered} selectedIds={tt.reviews.selectedIds} onToggleAll={tt.reviews.toggleAll} testid="reviews-select-all" />
                    <th className="p-4">Author</th>
                    <th className="p-4">Source</th>
                    <th className="p-4">Rating</th>
                    <th className="p-4">Excerpt</th>
                    <th className="p-4">Order</th>
                    <th className="p-4">Status</th>
                    <th className="p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {tt.reviews.filtered.length === 0 && <tr><td colSpan={8} className="p-10 text-center text-muted-foreground">{reviews.length === 0 ? "No reviews yet." : "No matches."}</td></tr>}
                  {tt.reviews.filtered.map((r) => (
                    <tr key={r.id} className="border-t border-border hover:bg-foreground/5" data-testid={`admin-review-row-${r.id}`}>
                      <SelectRowCell id={r.id} selectedIds={tt.reviews.selectedIds} onToggle={tt.reviews.toggle} testid={`reviews-select-${r.id}`} />
                      <td className="p-4">
                        <div className="font-medium">{r.author_name}</div>
                        <div className="text-xs text-muted-foreground">{[r.author_role, r.country].filter(Boolean).join(" · ") || "—"}</div>
                      </td>
                      <td className="p-4 text-xs font-mono uppercase tracking-wider">{r.source}</td>
                      <td className="p-4">
                        <div className="inline-flex items-center gap-0.5">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star key={i} className={`w-3.5 h-3.5 ${i < (r.rating || 5) ? "fill-yellow-400 text-yellow-400" : "text-foreground/20"}`} />
                          ))}
                        </div>
                      </td>
                      <td className="p-4 text-xs text-muted-foreground line-clamp-2 max-w-[380px]">{r.text}</td>
                      <td className="p-4">{r.order}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-mono ${r.published ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                          {r.published ? "Live" : "Draft"}
                        </span>
                      </td>
                      <td className="p-4 flex gap-2">
                        <button onClick={() => setEditingReview(r)} className="p-2 rounded hover:bg-foreground/10">Edit</button>
                        <button onClick={() => deleteReview(r.id)} className="p-2 rounded hover:bg-destructive/10 text-destructive"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "showcase-videos" && (
          <div className="space-y-4" data-testid="admin-showcase-videos">
            <div className="flex justify-between items-center gap-3 flex-wrap">
              <div>
                <h2 className="font-heading font-bold text-2xl">Video Showcase playlist</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Videos featured in the landing page &ldquo;See our work in motion&rdquo; player (just after Featured Projects). Paste any Bunny Stream iframe URL.
                </p>
              </div>
              <button onClick={() => setEditingShowcaseVideo(emptyShowcaseVideo)} className="rounded-full px-4 py-2 bg-primary text-primary-foreground text-sm inline-flex items-center gap-2" data-testid="admin-showcase-video-new">
                <Plus className="w-4 h-4" /> Add video
              </button>
            </div>
            <TableToolbar
              search={tt.showcaseVideos.search}
              onSearchChange={tt.showcaseVideos.setSearch}
              placeholder="Search videos by title, tag or URL…"
              selectedCount={tt.showcaseVideos.selectedIds.size}
              onBulkDelete={() => bulkDelete("showcaseVideos", "/admin/showcase-videos", "videos")}
              onClearSelection={tt.showcaseVideos.clear}
              testidPrefix="showcase-videos"
            />
            <div className="glass rounded-3xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="text-left text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground bg-foreground/5">
                  <tr>
                    <SelectAllCell items={tt.showcaseVideos.filtered} selectedIds={tt.showcaseVideos.selectedIds} onToggleAll={tt.showcaseVideos.toggleAll} testid="showcase-videos-select-all" />
                    <th className="p-4">Title</th>
                    <th className="p-4">Tag</th>
                    <th className="p-4">Duration</th>
                    <th className="p-4">URL</th>
                    <th className="p-4">Order</th>
                    <th className="p-4">Status</th>
                    <th className="p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {tt.showcaseVideos.filtered.length === 0 && <tr><td colSpan={8} className="p-10 text-center text-muted-foreground">{showcaseVideos.length === 0 ? "No showcase videos yet." : "No matches."}</td></tr>}
                  {tt.showcaseVideos.filtered.map((v) => (
                    <tr key={v.id} className="border-t border-border hover:bg-foreground/5" data-testid={`admin-showcase-video-row-${v.id}`}>
                      <SelectRowCell id={v.id} selectedIds={tt.showcaseVideos.selectedIds} onToggle={tt.showcaseVideos.toggle} testid={`showcase-videos-select-${v.id}`} />
                      <td className="p-4">
                        <div className="font-medium">{v.title}</div>
                        <div className="text-xs text-muted-foreground line-clamp-1 max-w-[320px]">{v.subtitle}</div>
                      </td>
                      <td className="p-4 text-xs font-mono uppercase tracking-wider">{v.tag || "—"}</td>
                      <td className="p-4 text-xs font-mono">{v.duration || "—"}</td>
                      <td className="p-4 text-xs text-muted-foreground truncate max-w-[220px]">{v.video_url}</td>
                      <td className="p-4">{v.order}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-mono ${v.published ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                          {v.published ? "Live" : "Draft"}
                        </span>
                      </td>
                      <td className="p-4 flex gap-2">
                        <button onClick={() => setEditingShowcaseVideo(v)} className="p-2 rounded hover:bg-foreground/10">Edit</button>
                        <button onClick={() => deleteShowcaseVideo(v.id)} className="p-2 rounded hover:bg-destructive/10 text-destructive"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "portfolio" && (
          <div className="space-y-4" data-testid="admin-portfolio">
            <div className="flex justify-between items-center gap-3 flex-wrap">
              <div>
                <h2 className="font-heading font-bold text-2xl">Portfolio items</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Selected work shown on the dedicated <code className="text-xs bg-muted px-1.5 py-0.5 rounded">/portfolio</code> page with sortable category filters.
                </p>
              </div>
              <button onClick={() => setEditingPortfolioItem(emptyPortfolioItem)} className="rounded-full px-4 py-2 bg-primary text-primary-foreground text-sm inline-flex items-center gap-2" data-testid="admin-portfolio-new">
                <Plus className="w-4 h-4" /> Add item
              </button>
            </div>
            <TableToolbar
              search={tt.portfolio.search}
              onSearchChange={tt.portfolio.setSearch}
              placeholder="Search portfolio by title, category, client, year…"
              selectedCount={tt.portfolio.selectedIds.size}
              onBulkDelete={() => bulkDelete("portfolio", "/admin/portfolio", "portfolio items")}
              onClearSelection={tt.portfolio.clear}
              testidPrefix="portfolio"
            />
            <div className="glass rounded-3xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="text-left text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground bg-foreground/5">
                  <tr>
                    <SelectAllCell items={tt.portfolio.filtered} selectedIds={tt.portfolio.selectedIds} onToggleAll={tt.portfolio.toggleAll} testid="portfolio-select-all" />
                    <th className="p-4">Preview</th>
                    <th className="p-4">Title</th>
                    <th className="p-4">Category</th>
                    <th className="p-4">Year</th>
                    <th className="p-4">Order</th>
                    <th className="p-4">Status</th>
                    <th className="p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {tt.portfolio.filtered.length === 0 && <tr><td colSpan={8} className="p-10 text-center text-muted-foreground">{portfolioItems.length === 0 ? "No portfolio items yet." : "No matches."}</td></tr>}
                  {tt.portfolio.filtered.map((p) => {
                    const cat = portfolioCategories.find((c) => c.slug === p.category_slug);
                    return (
                      <tr key={p.id} className="border-t border-border hover:bg-foreground/5" data-testid={`admin-portfolio-row-${p.id}`}>
                        <SelectRowCell id={p.id} selectedIds={tt.portfolio.selectedIds} onToggle={tt.portfolio.toggle} testid={`portfolio-select-${p.id}`} />
                        <td className="p-4">
                          <div className="w-16 h-12 rounded-lg overflow-hidden border border-border bg-foreground/5">
                            {p.cover_image ? <img src={p.cover_image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-[10px] text-muted-foreground">—</div>}
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="font-medium">{p.title}</div>
                          <div className="text-xs text-muted-foreground line-clamp-1 max-w-[320px]">{p.subtitle}</div>
                        </td>
                        <td className="p-4 text-xs font-mono uppercase tracking-wider">{cat?.name || p.category_slug || "—"}</td>
                        <td className="p-4 text-xs">{p.year || "—"}</td>
                        <td className="p-4">{p.order}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-mono ${p.published ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                            {p.published ? "Live" : "Draft"}
                          </span>
                        </td>
                        <td className="p-4 flex gap-2">
                          <button onClick={() => setEditingPortfolioItem(p)} className="p-2 rounded hover:bg-foreground/10">Edit</button>
                          <button onClick={() => deletePortfolioItem(p.id)} className="p-2 rounded hover:bg-destructive/10 text-destructive"><Trash2 className="w-4 h-4" /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "portfolio-cats" && (
          <div className="space-y-4" data-testid="admin-portfolio-cats">
            <div className="flex justify-between items-center gap-3 flex-wrap">
              <div>
                <h2 className="font-heading font-bold text-2xl">Portfolio categories</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Filter tabs shown on the <code className="text-xs bg-muted px-1.5 py-0.5 rounded">/portfolio</code> page. Slug is used to link items to categories — keep it URL-safe.
                </p>
              </div>
              <button onClick={() => setEditingPortfolioCategory(emptyPortfolioCategory)} className="rounded-full px-4 py-2 bg-primary text-primary-foreground text-sm inline-flex items-center gap-2" data-testid="admin-portfolio-cat-new">
                <Plus className="w-4 h-4" /> Add category
              </button>
            </div>
            <TableToolbar
              search={tt.portfolioCats.search}
              onSearchChange={tt.portfolioCats.setSearch}
              placeholder="Search categories…"
              selectedCount={tt.portfolioCats.selectedIds.size}
              onBulkDelete={() => bulkDelete("portfolioCats", "/admin/portfolio-categories", "categories")}
              onClearSelection={tt.portfolioCats.clear}
              testidPrefix="portfolio-cats"
            />
            <div className="glass rounded-3xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="text-left text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground bg-foreground/5">
                  <tr>
                    <SelectAllCell items={tt.portfolioCats.filtered} selectedIds={tt.portfolioCats.selectedIds} onToggleAll={tt.portfolioCats.toggleAll} testid="portfolio-cats-select-all" />
                    <th className="p-4">Name</th>
                    <th className="p-4">Slug</th>
                    <th className="p-4">Items</th>
                    <th className="p-4">Order</th>
                    <th className="p-4">Status</th>
                    <th className="p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {tt.portfolioCats.filtered.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-muted-foreground">{portfolioCategories.length === 0 ? "No categories yet." : "No matches."}</td></tr>}
                  {tt.portfolioCats.filtered.map((c) => (
                    <tr key={c.id} className="border-t border-border hover:bg-foreground/5" data-testid={`admin-portfolio-cat-row-${c.id}`}>
                      <SelectRowCell id={c.id} selectedIds={tt.portfolioCats.selectedIds} onToggle={tt.portfolioCats.toggle} testid={`portfolio-cats-select-${c.id}`} />
                      <td className="p-4 font-medium">{c.name}</td>
                      <td className="p-4 text-xs font-mono text-muted-foreground">{c.slug}</td>
                      <td className="p-4 text-xs">{portfolioItems.filter((i) => i.category_slug === c.slug).length}</td>
                      <td className="p-4">{c.order}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-mono ${c.published ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                          {c.published ? "Live" : "Draft"}
                        </span>
                      </td>
                      <td className="p-4 flex gap-2">
                        <button onClick={() => setEditingPortfolioCategory(c)} className="p-2 rounded hover:bg-foreground/10">Edit</button>
                        <button onClick={() => deletePortfolioCategory(c.id)} className="p-2 rounded hover:bg-destructive/10 text-destructive"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "service-videos" && (
          <ServiceVideosTab
            serviceMedia={serviceMedia}
            onSaved={(slug, url) => setServiceMedia((m) => ({ ...m, [slug]: url }))}
          />
        )}

        {tab === "product-videos" && (
          <ProductVideosTab
            productMedia={productMedia}
            onSaved={(slug, url) => setProductMedia((m) => ({ ...m, [slug]: url }))}
          />
        )}

        {tab === "page-videos" && (
          <PageVideosTab
            pageMedia={pageMedia}
            onSaved={(key, url) => setPageMedia((m) => ({ ...m, [key]: url }))}
          />
        )}

        {tab === "ai-agent" && (
          <div className="glass rounded-3xl p-6 md:p-8 space-y-6" data-testid="admin-ai-agent-tab">
            <div>
              <div className="font-heading font-bold text-xl">AI Agent · Training</div>
              <div className="text-xs text-muted-foreground mt-1">Everything the AI Agent knows when someone clicks &quot;Talk to AI Agent&quot;. Voice and text share the same knowledge.</div>
            </div>

            <div>
              <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">System prompt · personality</label>
              <textarea rows={6} value={training.system_prompt} onChange={(e) => setTraining({ ...training, system_prompt: e.target.value })}
                data-testid="ai-agent-system-prompt"
                className="mt-2 w-full bg-background border border-border rounded-xl px-4 py-3 text-sm font-mono"
                placeholder="You are the CloudLab AI Agent..." />
            </div>

            <div>
              <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Company knowledge · ground truth</label>
              <textarea rows={12} value={training.knowledge_text} onChange={(e) => setTraining({ ...training, knowledge_text: e.target.value })}
                data-testid="ai-agent-knowledge"
                className="mt-2 w-full bg-background border border-border rounded-xl px-4 py-3 text-sm"
                placeholder="Paste FAQs, pricing, SOPs, product specs. Or upload a PDF / DOCX / TXT below and we'll append it." />
              <div className="mt-2 text-[11px] text-muted-foreground">{training.knowledge_text.length.toLocaleString()} characters (~{Math.round(training.knowledge_text.length / 4).toLocaleString()} tokens)</div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button onClick={async () => {
                setTrainingBusy(true);
                try { await api.put("/admin/ai-agent/training", training); toast.success("Training saved."); }
                catch { toast.error("Save failed."); } finally { setTrainingBusy(false); }
              }} disabled={trainingBusy} data-testid="ai-agent-save"
                className="rounded-full px-5 py-2.5 bg-primary text-primary-foreground text-sm font-medium inline-flex items-center gap-2 disabled:opacity-70">
                <Save className="w-4 h-4" /> {trainingBusy ? "Saving…" : "Save training"}
              </button>
              <button onClick={() => trainingFileRef.current?.click()} data-testid="ai-agent-upload-file"
                className="rounded-full px-5 py-2.5 border border-border text-sm inline-flex items-center gap-2">
                <Plus className="w-4 h-4" /> Upload PDF / DOCX / TXT
              </button>
              <input ref={trainingFileRef} type="file" accept=".pdf,.docx,.txt,.md" className="hidden"
                data-testid="ai-agent-file-input"
                onChange={async (e) => {
                  const f = e.target.files?.[0]; if (!f) return;
                  const fd = new FormData(); fd.append("file", f);
                  try {
                    await api.post("/admin/ai-agent/training/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    toast.success(`Extracted from ${f.name}. Reload training to see it.`);
                    const t = await api.get("/admin/ai-agent/training");
                    setTraining({ system_prompt: t.data.system_prompt || "", knowledge_text: t.data.knowledge_text || "" });
                  } catch (err) { toast.error(err.response?.data?.detail || "Upload failed."); }
                  e.target.value = "";
                }} />
              <button onClick={() => setTraining({ ...training, knowledge_text: "" })} data-testid="ai-agent-clear"
                className="text-xs text-muted-foreground hover:text-destructive underline">
                Clear knowledge
              </button>
            </div>
          </div>
        )}

        {tab === "conversations" && (
          <div className="space-y-4" data-testid="admin-conversations">
            <div className="glass rounded-3xl p-6 md:p-8 border border-border">
              <div className="font-heading font-bold text-xl">AI Agent Conversations</div>
              <div className="text-xs text-muted-foreground mt-1">Every voice call and text chat is logged here with the visitor&apos;s contact details and full transcript.</div>
            </div>
            <TableToolbar
              search={tt.conversations.search}
              onSearchChange={tt.conversations.setSearch}
              placeholder="Search conversations by visitor, email, phone, channel…"
              selectedCount={tt.conversations.selectedIds.size}
              onBulkDelete={() => bulkDelete("conversations", "/admin/ai-agent/conversations", "conversations")}
              onClearSelection={tt.conversations.clear}
              testidPrefix="conversations"
            />
            <div className="glass rounded-3xl overflow-hidden" data-testid="admin-conversations-tab">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground bg-foreground/5">
                    <tr>
                      <SelectAllCell items={tt.conversations.filtered} selectedIds={tt.conversations.selectedIds} onToggleAll={tt.conversations.toggleAll} testid="conversations-select-all" />
                      <th className="p-4">Visitor</th>
                      <th className="p-4">Contact</th>
                      <th className="p-4">Channel</th>
                      <th className="p-4">Messages</th>
                      <th className="p-4">Started</th>
                      <th className="p-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tt.conversations.filtered.length === 0 && (
                      <tr><td colSpan={7} className="p-10 text-center text-muted-foreground">{conversations.length === 0 ? "No conversations yet — the AI Agent will log every chat and call here." : "No matches."}</td></tr>
                    )}
                    {tt.conversations.filtered.map((c) => (
                      <tr key={c.id} className="border-t border-border hover:bg-foreground/5" data-testid={`admin-conversation-row-${c.id}`}>
                        <SelectRowCell id={c.id} selectedIds={tt.conversations.selectedIds} onToggle={tt.conversations.toggle} testid={`conversations-select-${c.id}`} />
                        <td className="p-4"><div className="font-medium">{c.name || "Anonymous"}</div><div className="text-xs text-muted-foreground truncate max-w-[220px]">{c.question || "—"}</div></td>
                        <td className="p-4 text-xs"><div>{c.email || "—"}</div><div className="text-muted-foreground">{c.phone || "—"}</div></td>
                        <td className="p-4"><span className={`px-2 py-1 rounded-full text-xs font-mono ${c.channel === "voice" ? "bg-primary/15 text-primary" : "bg-foreground/10"}`}>{c.channel}</span></td>
                        <td className="p-4 text-xs">{c.message_count || (c.transcript || []).length}</td>
                        <td className="p-4 text-xs text-muted-foreground">{c.started_at ? new Date(c.started_at).toLocaleString() : "—"}</td>
                        <td className="p-4 text-right">
                          <button onClick={() => setViewingConversation(c)} data-testid={`admin-conversation-view-${c.id}`}
                            className="text-xs px-3 py-1.5 rounded-full border border-border hover:bg-foreground/10">View</button>
                          <button onClick={async () => {
                            if (!window.confirm("Delete this conversation?")) return;
                            try { await api.delete(`/admin/ai-agent/conversations/${c.id}`); toast.success("Deleted"); loadAll(); }
                            catch { toast.error("Delete failed"); }
                          }} data-testid={`admin-conversation-delete-${c.id}`}
                            className="ml-2 p-2 rounded hover:bg-destructive/10 text-destructive inline-flex"><Trash2 className="w-4 h-4" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {tab === "quotations" && (
          <div className="space-y-4" data-testid="admin-quotations">
            <TableToolbar
              search={tt.quotations.search}
              onSearchChange={tt.quotations.setSearch}
              placeholder="Search quotations by name, email, company, service, status…"
              selectedCount={tt.quotations.selectedIds.size}
              onBulkDelete={() => bulkDelete("quotations", "/quotations", "quotations")}
              onClearSelection={tt.quotations.clear}
              testidPrefix="quotations"
            />
            <div className="glass rounded-3xl overflow-hidden" data-testid="admin-quotes-table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground bg-foreground/5">
                    <tr>
                      <SelectAllCell items={tt.quotations.filtered} selectedIds={tt.quotations.selectedIds} onToggleAll={tt.quotations.toggleAll} testid="quotations-select-all" />
                      <th className="p-4">Name</th><th className="p-4">Company</th><th className="p-4">Service</th><th className="p-4">Budget</th><th className="p-4">Score</th><th className="p-4">Status</th><th className="p-4">Received</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tt.quotations.filtered.length === 0 && <tr><td colSpan={8} className="p-10 text-center text-muted-foreground">{quotes.length === 0 ? "No quotations yet." : "No matches."}</td></tr>}
                    {tt.quotations.filtered.map((q) => (
                      <tr key={q.id} className="border-t border-border hover:bg-foreground/5" data-testid={`admin-quote-row-${q.id}`}>
                        <SelectRowCell id={q.id} selectedIds={tt.quotations.selectedIds} onToggle={tt.quotations.toggle} testid={`quotations-select-${q.id}`} />
                        <td className="p-4"><div className="font-medium">{q.name}</div><div className="text-xs text-muted-foreground">{q.email}</div></td>
                        <td className="p-4">{q.company || "—"}</td>
                        <td className="p-4">{q.service}</td>
                        <td className="p-4">{q.budget}</td>
                        <td className="p-4"><span className={`px-2 py-1 rounded-full text-xs font-mono ${q.lead_score >= 80 ? "bg-primary/15 text-primary" : q.lead_score >= 60 ? "bg-yellow-500/15 text-yellow-600" : "bg-muted"}`}>{q.lead_score}</span></td>
                        <td className="p-4">
                          <select value={q.status} onChange={(e) => updateStatus(q.id, e.target.value)} className="bg-transparent border border-border rounded-full px-3 py-1 text-xs">
                            {["new","reviewing","quoted","won","lost"].map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </td>
                        <td className="p-4 text-xs text-muted-foreground">{new Date(q.created_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {tab === "estimates" && (
          <div className="space-y-4" data-testid="admin-estimates">
            <div>
              <h2 className="font-heading font-bold text-2xl">Estimate Requests</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Multi-step Project Estimate wizard submissions with full lead attribution (UTM, referrer, landing page, device).
              </p>
            </div>
            <TableToolbar
              search={tt.estimates.search}
              onSearchChange={tt.estimates.setSearch}
              placeholder="Search by name, company, email, project type, country, status…"
              selectedCount={tt.estimates.selectedIds.size}
              onBulkDelete={() => bulkDelete("estimates", "/admin/estimates", "estimate requests")}
              onClearSelection={tt.estimates.clear}
              testidPrefix="estimates"
            />
            <div className="glass rounded-3xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground bg-foreground/5">
                    <tr>
                      <SelectAllCell items={tt.estimates.filtered} selectedIds={tt.estimates.selectedIds} onToggleAll={tt.estimates.toggleAll} testid="estimates-select-all" />
                      <th className="p-4">Buyer</th>
                      <th className="p-4">Project</th>
                      <th className="p-4">Budget · Timeline</th>
                      <th className="p-4">Source</th>
                      <th className="p-4">Score</th>
                      <th className="p-4">Status</th>
                      <th className="p-4">Received</th>
                      <th className="p-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {tt.estimates.filtered.length === 0 && (
                      <tr><td colSpan={9} className="p-10 text-center text-muted-foreground">{estimates.length === 0 ? "No estimate requests yet." : "No matches."}</td></tr>
                    )}
                    {tt.estimates.filtered.map((e) => (
                      <tr key={e.id} className="border-t border-border hover:bg-foreground/5" data-testid={`admin-estimate-row-${e.id}`}>
                        <SelectRowCell id={e.id} selectedIds={tt.estimates.selectedIds} onToggle={tt.estimates.toggle} testid={`estimates-select-${e.id}`} />
                        <td className="p-4">
                          <div className="font-medium">{e.name}</div>
                          <div className="text-xs text-muted-foreground">{e.company || "—"} · {e.email}</div>
                          {e.country && <div className="text-[11px] text-muted-foreground">{e.country}</div>}
                        </td>
                        <td className="p-4 text-xs">
                          <div className="font-medium text-sm">{e.project_type || "—"}</div>
                          <div className="text-muted-foreground">{(e.stage || "—")}</div>
                        </td>
                        <td className="p-4 text-xs">
                          <div className="font-mono">{e.currency || ""} {e.budget_range || "—"}</div>
                          <div className="text-muted-foreground">{e.timeline || "—"}</div>
                        </td>
                        <td className="p-4 text-xs text-muted-foreground max-w-[200px]">
                          {e.attribution?.utm_source && <div className="font-mono">{e.attribution.utm_source}/{e.attribution.utm_medium || "—"}</div>}
                          {e.attribution?.landing_page && <div className="truncate">{e.attribution.landing_page}</div>}
                          {!e.attribution?.utm_source && !e.attribution?.landing_page && <span>—</span>}
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-mono ${e.lead_score >= 80 ? "bg-primary/15 text-primary" : e.lead_score >= 60 ? "bg-yellow-500/15 text-yellow-600" : "bg-muted"}`}>{e.lead_score}</span>
                        </td>
                        <td className="p-4">
                          <select
                            value={e.status}
                            onChange={async (ev) => {
                              try { await api.patch(`/admin/estimates/${e.id}`, { status: ev.target.value }); toast.success("Updated"); loadAll(); }
                              catch { toast.error("Update failed"); }
                            }}
                            className="bg-transparent border border-border rounded-full px-3 py-1 text-xs"
                            data-testid={`admin-estimate-status-${e.id}`}
                          >
                            {["new","reviewing","quoted","won","lost"].map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </td>
                        <td className="p-4 text-xs text-muted-foreground">{new Date(e.created_at).toLocaleString()}</td>
                        <td className="p-4 text-right whitespace-nowrap">
                          <button onClick={() => setViewingEstimate(e)} className="text-xs px-3 py-1.5 rounded-full border border-border hover:bg-foreground/10" data-testid={`admin-estimate-view-${e.id}`}>View</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {tab === "blogs" && (
          <div className="space-y-6">
            <form onSubmit={generateBlog} className="glass rounded-3xl p-6 space-y-4" data-testid="admin-generate-form">
              <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-primary" /><h3 className="font-heading font-bold text-lg">AI Article Engine · GPT-5.2</h3></div>
              <div className="grid md:grid-cols-2 gap-4">
                <div><label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Topic *</label>
                  <input required value={gen.topic} onChange={(e) => setGen({ ...gen, topic: e.target.value })} placeholder="e.g. How AI voice agents transform real estate lead qualification" className="mt-2 w-full bg-transparent border border-border rounded-xl px-4 py-3" data-testid="admin-gen-topic" /></div>
                <div><label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Category</label>
                  <input value={gen.category} onChange={(e) => setGen({ ...gen, category: e.target.value })} className="mt-2 w-full bg-transparent border border-border rounded-xl px-4 py-3" data-testid="admin-gen-category" /></div>
                <div><label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Target country</label>
                  <select value={gen.target_country} onChange={(e) => setGen({ ...gen, target_country: e.target.value })} className="mt-2 w-full bg-background border border-border rounded-xl px-4 py-3" data-testid="admin-gen-country">
                    {["United Kingdom","United States","Pakistan","Germany","Saudi Arabia","United Arab Emirates"].map(c => <option key={c}>{c}</option>)}
                  </select></div>
                <div><label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Length</label>
                  <select value={gen.length} onChange={(e) => setGen({ ...gen, length: e.target.value })} className="mt-2 w-full bg-background border border-border rounded-xl px-4 py-3" data-testid="admin-gen-length">
                    <option value="short">Short (600-800)</option><option value="medium">Medium (1200-1500)</option><option value="long">Long (2000-2500)</option>
                  </select></div>
                <div className="md:col-span-2"><label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Target keywords (comma-separated)</label>
                  <input value={gen.target_keywords} onChange={(e) => setGen({ ...gen, target_keywords: e.target.value })} placeholder="AI automation UK, agentic AI, enterprise AI" className="mt-2 w-full bg-transparent border border-border rounded-xl px-4 py-3" data-testid="admin-gen-keywords" /></div>
              </div>
              <button disabled={genLoading} type="submit" className="rounded-full px-6 py-3 bg-primary text-primary-foreground text-sm font-semibold btn-neon inline-flex items-center gap-2 disabled:opacity-50" data-testid="admin-gen-submit">
                {genLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating (30-60s)…</> : "Generate + Publish"}
              </button>
            </form>

            <TableToolbar
              search={tt.blogs.search}
              onSearchChange={tt.blogs.setSearch}
              placeholder="Search articles by title, category, slug…"
              selectedCount={tt.blogs.selectedIds.size}
              onBulkDelete={() => bulkDelete("blogs", "/admin/blogs", "articles")}
              onClearSelection={tt.blogs.clear}
              testidPrefix="blogs"
            />
            <div className="glass rounded-3xl overflow-hidden" data-testid="admin-blogs-table">
              <table className="w-full text-sm">
                <thead className="text-left text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground bg-foreground/5">
                  <tr>
                    <SelectAllCell items={tt.blogs.filtered} selectedIds={tt.blogs.selectedIds} onToggleAll={tt.blogs.toggleAll} testid="blogs-select-all" />
                    <th className="p-4">Cover</th><th className="p-4">Title</th><th className="p-4">Category</th><th className="p-4">Status</th><th className="p-4">Created</th><th className="p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {tt.blogs.filtered.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-muted-foreground">{blogs.length === 0 ? "No articles yet — generate your first above." : "No matches."}</td></tr>}
                  {tt.blogs.filtered.map((b) => (
                    <tr key={b.id} className="border-t border-border" data-testid={`admin-blog-row-${b.id}`}>
                      <SelectRowCell id={b.id} selectedIds={tt.blogs.selectedIds} onToggle={tt.blogs.toggle} testid={`blogs-select-${b.id}`} />
                      <td className="p-4">
                        <button
                          onClick={() => setEditingBlogCover(b)}
                          className="group block w-20 h-12 rounded-lg overflow-hidden border border-border bg-foreground/5 relative hover:border-primary/50"
                          title="Edit cover image — upload, paste URL, or generate with AI"
                          data-testid={`admin-blog-cover-${b.id}`}
                        >
                          {b.cover_image ? (
                            <img src={b.cover_image} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.opacity = "0"; }} />
                          ) : (
                            <div className="w-full h-full grid place-items-center text-[10px] font-mono text-muted-foreground">no cover</div>
                          )}
                          <div className="absolute inset-0 bg-primary/70 opacity-0 group-hover:opacity-100 transition-opacity grid place-items-center text-primary-foreground text-[10px] font-mono uppercase tracking-widest">Edit</div>
                        </button>
                        <button
                          onClick={() => setEditingBlogCover(b)}
                          className="mt-1 text-[10px] font-mono uppercase tracking-widest text-primary hover:underline"
                          data-testid={`admin-blog-cover-link-${b.id}`}
                        >
                          Upload / generate
                        </button>
                      </td>
                      <td className="p-4"><a href={`/blog/${b.slug}`} target="_blank" rel="noreferrer" className="hover:text-primary font-medium">{b.title}</a></td>
                      <td className="p-4">{b.category}</td>
                      <td className="p-4">
                        <button onClick={() => togglePublish(b)} className={`px-3 py-1 rounded-full text-xs font-mono ${b.published ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>{b.published ? "Published" : "Draft"}</button>
                      </td>
                      <td className="p-4 text-xs text-muted-foreground">{new Date(b.created_at).toLocaleDateString()}</td>
                      <td className="p-4"><button onClick={() => deleteBlog(b.id)} className="text-destructive hover:opacity-70"><Trash2 className="w-4 h-4" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "contacts" && (
          <div className="space-y-4" data-testid="admin-contacts">
            <TableToolbar
              search={tt.contacts.search}
              onSearchChange={tt.contacts.setSearch}
              placeholder="Search messages by sender, subject, content…"
              selectedCount={tt.contacts.selectedIds.size}
              onBulkDelete={() => bulkDelete("contacts", "/contact", "messages")}
              onClearSelection={tt.contacts.clear}
              testidPrefix="contacts"
            />
            <div className="glass rounded-3xl overflow-hidden" data-testid="admin-contacts-table">
              <table className="w-full text-sm">
                <thead className="text-left text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground bg-foreground/5">
                  <tr>
                    <SelectAllCell items={tt.contacts.filtered} selectedIds={tt.contacts.selectedIds} onToggleAll={tt.contacts.toggleAll} testid="contacts-select-all" />
                    <th className="p-4">From</th><th className="p-4">Subject</th><th className="p-4">Message</th><th className="p-4">Received</th>
                  </tr>
                </thead>
                <tbody>
                  {tt.contacts.filtered.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-muted-foreground">{contacts.length === 0 ? "No messages yet." : "No matches."}</td></tr>}
                  {tt.contacts.filtered.map((c) => (
                    <tr key={c.id} className="border-t border-border" data-testid={`admin-contact-row-${c.id}`}>
                      <SelectRowCell id={c.id} selectedIds={tt.contacts.selectedIds} onToggle={tt.contacts.toggle} testid={`contacts-select-${c.id}`} />
                      <td className="p-4"><div className="font-medium">{c.name}</div><div className="text-xs text-muted-foreground">{c.email}</div></td>
                      <td className="p-4">{c.subject || "—"}</td>
                      <td className="p-4 max-w-md text-muted-foreground">{c.message}</td>
                      <td className="p-4 text-xs text-muted-foreground">{new Date(c.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {editingProject && (
        <ProjectEditor
          initial={editingProject}
          portfolioCategories={portfolioCategories}
          onCancel={() => setEditingProject(null)}
          onSave={saveProject}
        />
      )}
      {editingJob && (
        <JobEditor
          initial={editingJob}
          onCancel={() => setEditingJob(null)}
          onSave={saveJob}
        />
      )}
      {editingNews && (
        <NewsEditor
          initial={editingNews}
          onCancel={() => setEditingNews(null)}
          onSave={saveNews}
        />
      )}
      {editingClient && (
        <ClientEditor
          initial={editingClient}
          onCancel={() => setEditingClient(null)}
          onSave={saveClient}
        />
      )}
      {editingRecognition && (
        <RecognitionEditor
          initial={editingRecognition}
          onCancel={() => setEditingRecognition(null)}
          onSave={saveRecognition}
        />
      )}
      {editingReview && (
        <ReviewEditor
          initial={editingReview}
          onCancel={() => setEditingReview(null)}
          onSave={saveReview}
        />
      )}
      {editingShowcaseVideo && (
        <ShowcaseVideoEditor
          initial={editingShowcaseVideo}
          portfolioCategories={portfolioCategories}
          onCancel={() => setEditingShowcaseVideo(null)}
          onSave={saveShowcaseVideo}
        />
      )}
      {editingPortfolioCategory && (
        <PortfolioCategoryEditor
          initial={editingPortfolioCategory}
          onCancel={() => setEditingPortfolioCategory(null)}
          onSave={savePortfolioCategory}
        />
      )}
      {editingPortfolioItem && (
        <PortfolioItemEditor
          initial={editingPortfolioItem}
          categories={portfolioCategories}
          onCancel={() => setEditingPortfolioItem(null)}
          onSave={savePortfolioItem}
        />
      )}
      {editingHeroWord && (
        <HeroWordEditor
          initial={editingHeroWord}
          onCancel={() => setEditingHeroWord(null)}
          onSave={saveHeroWord}
        />
      )}
      {editingNavItem && (
        <NavItemEditor
          initial={editingNavItem}
          onCancel={() => setEditingNavItem(null)}
          onSave={saveNavItem}
        />
      )}
      {managingSubmenusOf && (
        <SubmenuManager
          parent={managingSubmenusOf}
          items={navItems.filter((n) => n.parent_id === managingSubmenusOf.id)}
          onClose={() => setManagingSubmenusOf(null)}
          onEditItem={(item) => setEditingNavItem(item || { ...emptyNavItem, parent_id: managingSubmenusOf.id })}
          onToggle={toggleNavItemPublished}
          onDelete={deleteNavItem}
        />
      )}
      {viewingEstimate && (
        <EstimateDetail
          estimate={viewingEstimate}
          onClose={() => setViewingEstimate(null)}
        />
      )}
      {viewingConversation && (
        <ConversationViewer
          conversation={viewingConversation}
          onClose={() => setViewingConversation(null)}
        />
      )}
      {editingBlogCover && (
        <BlogCoverEditor
          blog={editingBlogCover}
          onClose={() => setEditingBlogCover(null)}
          onSaved={(cover) => {
            setBlogs((list) => list.map((b) => (b.id === editingBlogCover.id ? { ...b, cover_image: cover } : b)));
            setEditingBlogCover(null);
          }}
        />
      )}
        </div>
      </div>
  );
}

function ServiceVideosTab({ serviceMedia, onSaved }) {
  return (
    <MediaLibraryTab
      kind="service"
      items={SERVICES}
      map={serviceMedia}
      onSaved={onSaved}
      title="Service hero videos"
      description="Paste a portrait (9:16 mobile) video URL for each service. Direct MP4/WebM links autoplay; YouTube, Vimeo and Bunny Stream (mediadelivery.net) links auto-embed. Leave blank to fall back to the static hero image."
    />
  );
}

function ProductVideosTab({ productMedia, onSaved }) {
  return (
    <MediaLibraryTab
      kind="product"
      items={PRODUCTS}
      map={productMedia}
      onSaved={onSaved}
      title="Product hero videos"
      description="Paste a portrait (9:16 mobile) video URL for each product. Direct MP4/WebM links autoplay; YouTube, Vimeo and Bunny Stream (mediadelivery.net) links auto-embed. When set, the video replaces the hero image / inline demo card on the product page."
    />
  );
}

// Page hero videos are keyed (partnership-hero, about-hero, careers-hero, etc.)
const PAGE_MEDIA_KEYS = [
  { key: "partnership-hero", name: "Partnership hero", note: "Landscape 16:9 preferred" },
  { key: "about-hero",       name: "About hero",       note: "Landscape 16:9 preferred" },
  { key: "careers-hero",     name: "Careers hero",     note: "Landscape 16:9 preferred" },
  { key: "home-hero",        name: "Home hero",        note: "Landscape 16:9 preferred" },
];

function PageVideosTab({ pageMedia, onSaved }) {
  return (
    <div className="glass rounded-3xl overflow-hidden" data-testid="admin-page-videos-tab">
      <div className="p-6 md:p-8 border-b border-border">
        <div className="font-heading font-bold text-xl">Page hero videos</div>
        <div className="text-xs text-muted-foreground mt-1">
          Paste a landscape (16:9) video URL for each page hero. Direct MP4/WebM links autoplay; YouTube, Vimeo and Bunny Stream (mediadelivery.net) links auto-embed. Leave blank to fall back to the animated aurora placeholder.
        </div>
      </div>
      <div className="p-6 md:p-8 space-y-4">
        {PAGE_MEDIA_KEYS.map((it) => (
          <PageVideoRow key={it.key} it={it} current={pageMedia[it.key] || ""} onSaved={onSaved} />
        ))}
      </div>
    </div>
  );
}

function PageVideoRow({ it, current, onSaved }) {
  const [url, setUrl] = useState(current);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setUrl(current); }, [current]);
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/admin/page-media", { key: it.key, video_url: url });
      onSaved?.(it.key, url);
      toast.success(`${it.name} · video saved`);
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };
  const clear = async () => {
    if (!window.confirm(`Remove video for ${it.name}?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/page-media/${it.key}`);
      setUrl("");
      onSaved?.(it.key, "");
      toast.success(`${it.name} · video removed`);
    } catch { toast.error("Remove failed"); }
    finally { setBusy(false); }
  };
  const isDirty = url !== current;
  return (
    <div className="grid md:grid-cols-[220px_1fr_auto] gap-3 md:gap-4 items-center p-4 rounded-2xl border border-border bg-card" data-testid={`page-video-row-${it.key}`}>
      <div className="min-w-0">
        <div className="font-medium leading-tight truncate">{it.name}</div>
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground mt-0.5">{it.note}</div>
      </div>
      <input
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="https://…mp4 or https://youtu.be/… or https://player.mediadelivery.net/…"
        className="w-full bg-background border border-border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
        data-testid={`page-video-input-${it.key}`}
      />
      <div className="flex gap-2 md:justify-end">
        <button
          onClick={save} disabled={!isDirty || busy}
          className="rounded-full px-4 py-2 bg-primary text-primary-foreground text-xs inline-flex items-center gap-2 disabled:opacity-40"
          data-testid={`page-video-save-${it.key}`}
        >
          <Save className="w-3.5 h-3.5" /> Save
        </button>
        {current && (
          <button
            onClick={clear} disabled={busy}
            className="rounded-full px-3 py-2 border border-border text-xs hover:bg-destructive/10 hover:text-destructive"
            data-testid={`page-video-clear-${it.key}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function MediaLibraryTab({ kind, items, map, onSaved, title, description }) {
  return (
    <div className="glass rounded-3xl overflow-hidden" data-testid={`admin-${kind}-videos-tab`}>
      <div className="p-6 md:p-8 border-b border-border">
        <div className="font-heading font-bold text-xl">{title}</div>
        <div className="text-xs text-muted-foreground mt-1">{description}</div>
      </div>
      <div className="p-6 md:p-8 space-y-4">
        {items.map((it) => (
          <MediaVideoRow
            key={it.slug}
            kind={kind}
            svc={it}
            current={map[it.slug] || ""}
            onSaved={onSaved}
          />
        ))}
      </div>
    </div>
  );
}

function MediaVideoRow({ kind, svc, current, onSaved }) {
  const [url, setUrl] = useState(current);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setUrl(current); }, [current]);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/${kind}s/media`, { slug: svc.slug, video_url: url });
      onSaved?.(svc.slug, url);
      toast.success(`${svc.name} · video saved`);
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };
  const clear = async () => {
    if (!window.confirm(`Remove video for ${svc.name}?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/${kind}s/media/${svc.slug}`);
      setUrl("");
      onSaved?.(svc.slug, "");
      toast.success(`${svc.name} · video removed`);
    } catch { toast.error("Remove failed"); }
    finally { setBusy(false); }
  };
  const isDirty = url !== current;
  return (
    <div className="grid md:grid-cols-[220px_1fr_auto] gap-3 md:gap-4 items-center p-4 rounded-2xl border border-border bg-card" data-testid={`${kind}-video-row-${svc.slug}`}>
      <div className="min-w-0">
        <div className="font-medium leading-tight truncate">{svc.name}</div>
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground mt-0.5">/{svc.slug}</div>
      </div>
      <input
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="https://…mp4 or https://youtu.be/… or https://player.mediadelivery.net/…"
        className="w-full bg-background border border-border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
        data-testid={`${kind}-video-input-${svc.slug}`}
      />
      <div className="flex gap-2 md:justify-end">
        <button
          onClick={save} disabled={!isDirty || busy}
          className="rounded-full px-4 py-2 bg-primary text-primary-foreground text-xs inline-flex items-center gap-2 disabled:opacity-40"
          data-testid={`${kind}-video-save-${svc.slug}`}
        >
          <Save className="w-3.5 h-3.5" /> Save
        </button>
        {current && (
          <button
            onClick={clear} disabled={busy}
            className="rounded-full px-3 py-2 border border-border text-xs hover:bg-destructive/10 hover:text-destructive"
            data-testid={`${kind}-video-clear-${svc.slug}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function BlogCoverEditor({ blog, onClose, onSaved }) {
  const [url, setUrl] = useState(blog.cover_image || "");
  const [prompt, setPrompt] = useState(blog.title || "");
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/blogs/${blog.id}`, { cover_image: url });
      onSaved?.(url);
      toast.success("Cover saved");
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const generate = async () => {
    if (!prompt.trim()) return toast.error("Add a prompt first");
    setGenerating(true);
    toast.info("Generating cover — this can take up to a minute…");
    try {
      const { data } = await api.post("/admin/blogs/generate-image", { prompt, blog_id: blog.id });
      setUrl(data.cover_image);
      onSaved?.(data.cover_image);
      toast.success("Cover generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file (JPG, PNG, WEBP).");
    if (file.size > 8 * 1024 * 1024) return toast.error("Image is larger than 8 MB. Please compress it first.");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUrl(data.url);
      // auto-save the newly uploaded cover
      await api.patch(`/admin/blogs/${blog.id}`, { cover_image: data.url });
      onSaved?.(data.url);
      toast.success("Cover uploaded & saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = async (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (f) await uploadFile(f);
  };

  const onPaste = async (e) => {
    for (const it of e.clipboardData?.items || []) {
      if (it.type?.startsWith("image/")) {
        const blob = it.getAsFile();
        if (blob) { e.preventDefault(); await uploadFile(blob); return; }
      }
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-background/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="admin-blog-cover-editor">
      <div className="glass rounded-3xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="p-6 border-b border-border flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-primary mb-1">Blog cover · {blog.slug}</div>
            <div className="font-heading font-bold text-lg truncate">{blog.title}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-foreground/10" data-testid="admin-blog-cover-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Preview */}
          <div className="rounded-2xl border border-border overflow-hidden bg-foreground/5 aspect-video">
            {url ? (
              <img src={url} alt="Preview" className="w-full h-full object-cover" data-testid="admin-blog-cover-preview" />
            ) : (
              <div className="w-full h-full grid place-items-center text-sm text-muted-foreground">
                No cover yet — upload one below, paste a URL, or generate with AI.
              </div>
            )}
          </div>

          {/* Manual file upload */}
          <div className="rounded-2xl border border-secondary/30 bg-secondary/[0.04] p-5">
            <div className="flex items-center gap-2 mb-3">
              <UploadCloud className="w-4 h-4 text-secondary" />
              <div className="font-heading font-bold text-sm">Upload from your computer</div>
            </div>
            <div
              onDrop={onDrop}
              onDragOver={(e) => e.preventDefault()}
              onPaste={onPaste}
              tabIndex={0}
              className="rounded-xl border-2 border-dashed border-border bg-background/50 p-6 text-center outline-none focus:border-secondary/60 transition-colors"
              data-testid="admin-blog-cover-dropzone"
            >
              <UploadCloud className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <div className="text-sm font-medium">Drag &amp; drop, paste, or click to browse</div>
              <div className="text-[11px] text-muted-foreground mt-1">
                <b>Recommended: 1200 × 630px</b> (16:9) · JPG, PNG or WEBP · Max 8 MB
              </div>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="mt-3 rounded-full px-4 py-2 bg-secondary text-secondary-foreground text-xs inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="admin-blog-cover-upload-btn"
              >
                {uploading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…</> : <><UploadCloud className="w-3.5 h-3.5" /> Choose image</>}
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                data-testid="admin-blog-cover-file-input"
                onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0])}
              />
              <div className="text-[10px] text-muted-foreground mt-3">
                Tip: press <kbd className="px-1.5 py-0.5 rounded bg-foreground/10 font-mono">Ctrl / ⌘ + V</kbd> to paste a screenshot straight from your clipboard.
              </div>
            </div>
          </div>

          {/* Manual URL */}
          <div>
            <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Or paste an image URL</label>
            <div className="mt-2 flex gap-2">
              <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://…jpg / .png / .webp"
                className="flex-1 bg-background border border-border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
                data-testid="admin-blog-cover-url"
              />
              <button
                onClick={save}
                disabled={busy}
                className="rounded-full px-4 py-2 bg-primary text-primary-foreground text-xs inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="admin-blog-cover-save"
              >
                <Save className="w-3.5 h-3.5" /> Save URL
              </button>
            </div>
            <div className="text-[11px] text-muted-foreground mt-1.5">
              Paste any public image URL (Unsplash, Bunny, your CDN, etc.).
            </div>
          </div>

          {/* AI generate */}
          <div className="rounded-2xl border border-primary/30 bg-primary/[0.04] p-5">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-primary" />
              <div className="font-heading font-bold text-sm">Or generate with AI · gpt-image-1</div>
            </div>
            <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Prompt</label>
            <textarea
              rows={3}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Describe the image — e.g. Cinematic editorial photo of a modern London office at dusk, AI dashboards glowing on screens, 16:9 landscape."
              className="mt-2 w-full bg-background border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 resize-none"
              data-testid="admin-blog-cover-prompt"
            />
            <button
              onClick={generate}
              disabled={generating}
              className="mt-3 rounded-full px-5 py-2.5 bg-primary text-primary-foreground text-xs inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="admin-blog-cover-generate"
            >
              {generating ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating (up to 60s)…</> : <><Sparkles className="w-3.5 h-3.5" /> Generate cover</>}
            </button>
            <div className="text-[11px] text-muted-foreground mt-2">
              Uses OpenAI gpt-image-1. Landscape 16:9 with a cinematic editorial style.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ConversationViewer({ conversation, onClose }) {
  const c = conversation;
  return (
    <div className="fixed inset-0 z-[100] bg-background/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="admin-conversation-viewer">
      <div className="glass rounded-3xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-border flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-primary mb-1">{c.channel} · {c.message_count || (c.transcript || []).length} messages</div>
            <div className="font-heading font-bold text-xl truncate">{c.name || "Anonymous"}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {c.email || "—"} · {c.phone || "—"}
            </div>
            {c.question && <div className="text-sm mt-2 italic text-muted-foreground">Initial context: &ldquo;{c.question}&rdquo;</div>}
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-foreground/10" data-testid="admin-conversation-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-3">
          {(!c.transcript || c.transcript.length === 0) && (
            <div className="text-center text-sm text-muted-foreground py-10">No transcript captured for this session.</div>
          )}
          {(c.transcript || []).map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-foreground/10"}`}>
                <div className="text-[10px] font-mono uppercase tracking-[0.15em] opacity-60 mb-1">{m.role}</div>
                {m.content}
              </div>
            </div>
          ))}
        </div>
        <div className="p-4 border-t border-border text-xs text-muted-foreground flex items-center justify-between">
          <span>Started {c.started_at ? new Date(c.started_at).toLocaleString() : "—"}</span>
          <span>Ended reason: {c.ended_reason || "—"}</span>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  const cls = accent === "primary" ? "text-primary" : accent === "secondary" ? "text-secondary" : "text-foreground";
  return (
    <div className="glass rounded-2xl p-5">
      <div className={`font-heading font-black text-3xl ${cls}`}>{value ?? 0}</div>
      <div className="mt-1 text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
    </div>
  );
}

// ---- Reusable Image Uploader (browse / drag&drop / paste + auto-resize) ----
async function resizeImageMax(fileOrBlob, maxW, maxH, mime = "image/jpeg", quality = 0.85) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("read failed"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("decode failed"));
      img.onload = () => {
        const ratio = Math.min(maxW / img.width, maxH / img.height, 1);
        const w = Math.max(1, Math.round(img.width * ratio));
        const h = Math.max(1, Math.round(img.height * ratio));
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL(mime, quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(fileOrBlob);
  });
}

function ImageUploader({ label, hint, value, onChange, testid, maxW = 1600, maxH = 1600, aspect = "16 / 9" }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleFile = async (file) => {
    if (!file || !file.type.startsWith("image/")) { toast.error("Please choose an image."); return; }
    setUploading(true);
    try {
      const dataUrl = await resizeImageMax(file, maxW, maxH);
      onChange(dataUrl);
      toast.success(`Image optimized (max ${maxW}×${maxH})`);
    } catch { toast.error("Could not process image."); }
    finally { setUploading(false); }
  };
  const onPaste = async (e) => {
    for (const it of e.clipboardData?.items || []) {
      if (it.type?.startsWith("image/")) {
        const blob = it.getAsFile();
        if (blob) { e.preventDefault(); await handleFile(blob); return; }
      }
    }
  };
  const onDrop = async (e) => { e.preventDefault(); if (e.dataTransfer?.files?.[0]) await handleFile(e.dataTransfer.files[0]); };

  return (
    <div>
      <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
      {hint && <div className="mt-1 text-[11px] text-muted-foreground">{hint}</div>}
      <div
        onDrop={onDrop}
        onDragOver={(e) => e.preventDefault()}
        onPaste={onPaste}
        tabIndex={0}
        data-testid={`${testid}-zone`}
        className="mt-2 rounded-xl border-2 border-dashed border-border bg-muted/20 p-4 outline-none focus:border-primary/60 transition-colors"
      >
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            data-testid={`${testid}-browse`}
            className="rounded-full px-4 py-2 bg-primary text-primary-foreground text-sm font-medium"
          >
            {uploading ? "Processing…" : "Browse & upload"}
          </button>
          <span className="text-xs text-muted-foreground">
            or <b>drag & drop</b> · click here and press <kbd className="px-1.5 py-0.5 rounded bg-foreground/10 text-[10px] font-mono">Ctrl / ⌘ + V</kbd> to paste
          </span>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          data-testid={`${testid}-file`}
          onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
        />
      </div>
      <input
        value={value && !String(value).startsWith("data:") ? value : ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="…or paste an image URL"
        className="mt-2 w-full bg-background border border-border rounded-xl px-4 py-2.5 text-sm"
        data-testid={`${testid}-url`}
      />
      {value && (
        <div className="mt-3 rounded-xl border border-border overflow-hidden bg-muted/30 flex items-center justify-between gap-3">
          <div className="p-2" style={{ width: 160 }}>
            <div style={{ aspectRatio: aspect }} className="w-full overflow-hidden rounded-md bg-muted">
              <img src={value} alt="preview" className="w-full h-full object-cover" data-testid={`${testid}-preview`} />
            </div>
          </div>
          <button
            type="button"
            onClick={() => onChange("")}
            data-testid={`${testid}-clear`}
            className="mr-3 text-xs text-muted-foreground hover:text-destructive underline"
          >
            Remove
          </button>
        </div>
      )}
    </div>
  );
}

// ---- Project Editor Modal (Phase 4.0.1 — tabbed, case-study-aware) ----
const SERVICE_OPTIONS = [
  { slug: "ai-development", name: "AI Development" },
  { slug: "ai-agent-development", name: "AI Agent Development" },
  { slug: "custom-software-development", name: "Custom Software Development" },
  { slug: "saas-development", name: "SaaS Development" },
  { slug: "crm-development", name: "CRM Development" },
  { slug: "mobile-app-development", name: "Mobile App Development" },
  { slug: "web-application-development", name: "Web Application Development" },
  { slug: "edtech-development", name: "EdTech Development" },
  { slug: "mvp-development", name: "MVP Development" },
  { slug: "software-modernisation", name: "Software Modernisation" },
];
const INDUSTRY_OPTIONS = [
  "education", "healthcare", "real-estate", "construction", "hospitality",
  "manufacturing", "retail", "financial-services", "travel", "logistics", "government",
];
const MARKET_OPTIONS = [
  { slug: "uk", name: "United Kingdom" }, { slug: "us", name: "United States" },
  { slug: "pk", name: "Pakistan" }, { slug: "ae", name: "United Arab Emirates" },
  { slug: "sa", name: "Saudi Arabia" }, { slug: "de", name: "Germany" },
  { slug: "europe", name: "Europe" },
];
const PUB_STATES = ["DRAFT", "READY_FOR_REVIEW", "PUBLISHED", "ARCHIVED"];

function slugifyClient(s) {
  return (s || "").toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 80);
}

function ProjectEditor({ initial, portfolioCategories = [], onCancel, onSave }) {
  const isEdit = !!initial.id;
  const [tab, setTab] = useState("basic");
  const [validation, setValidation] = useState(null);  // { errors, warnings, info, ready_to_publish }
  const [validating, setValidating] = useState(false);
  const [f, setF] = useState(() => ({
    ...initial,
    // CSV helpers for free-text arrays
    tech_stack_str: (initial.tech_stack || []).join(", "),
    technologies_str: (initial.technologies || []).join(", "),
    cloud_lab_role_str: (initial.cloud_lab_role || []).join("\n"),
    results_str: (initial.results || []).map((r) => `${r.metric} = ${r.value}`).join("\n"),
    // Structured arrays — stored as objects, edited via mini-sub-editors
    capabilities: initial.capabilities || [],
    workflow: initial.workflow || [],
    metrics: initial.metrics || [],
    testimonials: initial.testimonials || [],
    services: initial.services || [],
    industries: initial.industries || [],
    markets: initial.markets || [],
    related_projects: initial.related_projects || [],
    related_projects_str: (initial.related_projects || []).join(", "),
    seo: initial.seo || {},
    answer_profile: initial.answer_profile || {},
    publication_status: initial.publication_status || "DRAFT",
    slug_override: initial.slug_override || (initial.slug ? "" : ""),
    // Cross-publish to Portfolio
    publish_to_portfolio: !!initial.linked_portfolio_slug,
    portfolio_category_slug: initial.portfolio_category_slug || "",
  }));

  const toggleArr = (key, value) => {
    setF((prev) => {
      const arr = prev[key] || [];
      return { ...prev, [key]: arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value] };
    });
  };

  const addRow = (key, row) => setF((prev) => ({ ...prev, [key]: [...(prev[key] || []), row] }));
  const updateRow = (key, idx, patch) => setF((prev) => ({
    ...prev,
    [key]: (prev[key] || []).map((r, i) => (i === idx ? { ...r, ...patch } : r)),
  }));
  const removeRow = (key, idx) => setF((prev) => ({
    ...prev,
    [key]: (prev[key] || []).filter((_, i) => i !== idx),
  }));

  const buildPayload = () => {
    const results = (f.results_str || "").split("\n").map((line) => {
      const [metric, value] = line.split("=").map((s) => s?.trim());
      return metric && value ? { metric, value } : null;
    }).filter(Boolean);
    return {
      ...(f.id ? { id: f.id } : {}),
      // Basic
      name: f.name,
      client: f.client || null,
      industry: f.industry,
      country: f.country,
      cover_image: f.cover_image,
      thumbnail_image: f.thumbnail_image || null,
      tagline: f.tagline,
      problem: f.problem || "",
      approach: f.approach || "",
      tech_stack: (f.tech_stack_str || "").split(",").map((s) => s.trim()).filter(Boolean),
      results,
      order: Number(f.order) || 100,
      published: !!f.published,
      publish_to_portfolio: !!f.publish_to_portfolio,
      portfolio_category_slug: f.publish_to_portfolio ? (f.portfolio_category_slug || null) : null,
      // Phase 4.0.1
      publish_as_case_study: !!f.publish_as_case_study,
      publication_status: f.publication_status || "DRAFT",
      slug_override: (f.slug_override || "").trim() || null,
      services: f.services || [],
      industries: f.industries || [],
      markets: f.markets || [],
      short_description: f.short_description || null,
      hero_subtitle: f.hero_subtitle || null,
      challenge: f.challenge || null,
      solution: f.solution || null,
      solution_type: f.solution_type || null,
      platform: f.platform || null,
      external_url: f.external_url || null,
      technologies: (f.technologies_str || "").split(",").map((s) => s.trim()).filter(Boolean),
      capabilities: (f.capabilities || []).filter((c) => c.title || c.desc),
      workflow: (f.workflow || []).filter((w) => w.title || w.desc),
      cloud_lab_role: (f.cloud_lab_role_str || "").split("\n").map((s) => s.trim()).filter(Boolean),
      metrics: f.metrics || [],
      testimonials: f.testimonials || [],
      seo: f.seo || {},
      answer_profile: f.answer_profile || {},
      hero_image: f.hero_image || null,
      gallery: f.gallery || [],
      related_projects: (f.related_projects_str || "").split(",").map((s) => s.trim()).filter(Boolean),
    };
  };

  const submit = (e) => {
    e.preventDefault();
    onSave(buildPayload());
  };

  const runValidate = async () => {
    if (!isEdit) {
      toast.info("Save the project first, then run validation.");
      return;
    }
    setValidating(true);
    try {
      const r = await api.post(`/admin/projects/${f.id}/validate`);
      setValidation(r.data);
      if (r.data.ready_to_publish) toast.success("Validation passed.");
      else toast.error(`Validation found ${r.data.errors?.length || 0} error(s).`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Validation call failed.");
    } finally {
      setValidating(false);
    }
  };

  const previewHref = f.slug
    ? `/case-studies/${f.slug_override?.trim() || f.slug}?preview=1`
    : null;

  // Readiness pills (quick glance)
  const seoReady = !!(f.seo?.title || f.name) && !!(f.seo?.description || f.short_description || f.tagline);
  const approvedMetricCount = (f.metrics || []).filter((m) => m.approved_for_public_display).length;
  const evidenceReady = approvedMetricCount > 0;

  return (
    <Modal onCancel={onCancel} title={f.id ? "Edit project / case study" : "New project"} testid="project-editor" wide>
      {/* --- Status strip --- */}
      <div className="mb-5 flex flex-wrap gap-2 items-center text-xs" data-testid="project-status-strip">
        <StatusPill ok={!!f.name && !!f.cover_image && !!f.tagline} label="BASIC" />
        <StatusPill ok={seoReady} label="SEO" />
        <StatusPill ok={evidenceReady} label={`EVIDENCE (${approvedMetricCount})`} />
        <span className={`px-2 py-1 rounded-full font-mono uppercase tracking-wider border ${
          f.publication_status === "PUBLISHED" ? "border-emerald-500/40 text-emerald-500 bg-emerald-500/10" :
          f.publication_status === "READY_FOR_REVIEW" ? "border-amber-500/40 text-amber-600 bg-amber-500/10" :
          f.publication_status === "ARCHIVED" ? "border-foreground/30 text-muted-foreground bg-foreground/5" :
          "border-foreground/30 text-muted-foreground bg-foreground/5"
        }`}>{f.publication_status}</span>
        {f.publish_as_case_study && (
          <span className="px-2 py-1 rounded-full font-mono uppercase tracking-wider border border-primary/40 text-primary bg-primary/10">
            Case Study
          </span>
        )}
      </div>

      {/* --- Tab nav --- */}
      <div className="mb-4 flex flex-wrap gap-1 border-b border-border overflow-x-auto" data-testid="project-tabs">
        {[
          ["basic", "Basic"],
          ["classification", "Classification"],
          ["story", "Story"],
          ["capabilities", "Capabilities"],
          ["evidence", "Evidence & Metrics"],
          ["seo", "SEO"],
          ["geo", "GEO / ARO"],
          ["media", "Media"],
          ["publishing", "Publishing"],
        ].map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            data-testid={`project-tab-${key}`}
            className={`px-3.5 py-2 text-sm whitespace-nowrap -mb-px border-b-2 ${
              tab === key ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >{label}</button>
        ))}
      </div>

      <form onSubmit={submit} className="space-y-5">
        {/* ============ BASIC ============ */}
        <div hidden={tab !== "basic"} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Name *" required value={f.name} onChange={(v) => setF({ ...f, name: v })} testid="pf-name" />
            <Field label="Client (optional)" value={f.client || ""} onChange={(v) => setF({ ...f, client: v })} testid="pf-client" />
            <Field label="Legacy industry (free text)" value={f.industry} onChange={(v) => setF({ ...f, industry: v })} testid="pf-industry" />
            <Field label="Country (legacy, free text)" value={f.country} onChange={(v) => setF({ ...f, country: v })} testid="pf-country" />
            <Field label="Tagline *" required value={f.tagline} onChange={(v) => setF({ ...f, tagline: v })} testid="pf-tagline" className="md:col-span-2" />
            <Field label="Short description (card + meta)" value={f.short_description || ""} onChange={(v) => setF({ ...f, short_description: v })} testid="pf-short-desc" className="md:col-span-2" />
            <Field label="Hero subtitle (longer hero paragraph)" value={f.hero_subtitle || ""} onChange={(v) => setF({ ...f, hero_subtitle: v })} testid="pf-hero-sub" className="md:col-span-2" />
            <Field label="Slug override (optional)" value={f.slug_override || ""} onChange={(v) => setF({ ...f, slug_override: v })} testid="pf-slug" placeholder={f.slug || slugifyClient(f.name)} />
            <Field label="Sort order" type="number" value={String(f.order)} onChange={(v) => setF({ ...f, order: v })} testid="pf-order" />
            <Field label="External product URL" value={f.external_url || ""} onChange={(v) => setF({ ...f, external_url: v })} testid="pf-external" placeholder="https://…" className="md:col-span-2" />
          </div>
        </div>

        {/* ============ CLASSIFICATION ============ */}
        <div hidden={tab !== "classification"} className="space-y-5">
          <ChecklistField
            label="Services (controlled)"
            hint="Select every service this case study is genuine evidence of. Appears on each service page's Related Case Studies."
            options={SERVICE_OPTIONS.map((o) => ({ value: o.slug, label: o.name }))}
            value={f.services}
            onToggle={(v) => toggleArr("services", v)}
            testid="pf-services"
          />
          <ChecklistField
            label="Industries (controlled)"
            hint="Canonical industry slug(s). Drives /industries filters."
            options={INDUSTRY_OPTIONS.map((o) => ({ value: o, label: o }))}
            value={f.industries}
            onToggle={(v) => toggleArr("industries", v)}
            testid="pf-industries"
          />
          <ChecklistField
            label="Markets (approved only)"
            hint="Explicit — do NOT infer from country. Only markets with real delivery evidence should be checked."
            options={MARKET_OPTIONS.map((o) => ({ value: o.slug, label: o.name }))}
            value={f.markets}
            onToggle={(v) => toggleArr("markets", v)}
            testid="pf-markets"
          />
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Solution type (e.g. AI-native CRM)" value={f.solution_type || ""} onChange={(v) => setF({ ...f, solution_type: v })} testid="pf-sol-type" />
            <Field label="Platform (e.g. Web SaaS)" value={f.platform || ""} onChange={(v) => setF({ ...f, platform: v })} testid="pf-platform" />
            <Field label="Related case-study slugs (comma)" value={f.related_projects_str || ""} onChange={(v) => setF({ ...f, related_projects_str: v })} testid="pf-related" placeholder="unicrm-ai, teachingsoftware-ai" className="md:col-span-2" />
          </div>
        </div>

        {/* ============ STORY ============ */}
        <div hidden={tab !== "story"} className="space-y-4">
          <FieldArea label="Challenge (plain English — the operational problem)" value={f.challenge || ""} onChange={(v) => setF({ ...f, challenge: v })} rows={5} testid="pf-challenge" />
          <FieldArea label="Solution (what CloudLab built)" value={f.solution || ""} onChange={(v) => setF({ ...f, solution: v })} rows={5} testid="pf-solution" />
          <div className="rounded-xl bg-muted/40 p-4 text-xs text-muted-foreground">
            Legacy &quot;Problem / Approach&quot; still save for backward compat (shown below). Prefer Challenge / Solution for Phase 4.0.1 case studies.
          </div>
          <FieldArea label="Legacy — The problem" value={f.problem} onChange={(v) => setF({ ...f, problem: v })} rows={3} testid="pf-problem" />
          <FieldArea label="Legacy — Our approach" value={f.approach} onChange={(v) => setF({ ...f, approach: v })} rows={3} testid="pf-approach" />
        </div>

        {/* ============ CAPABILITIES ============ */}
        <div hidden={tab !== "capabilities"} className="space-y-5">
          <RowEditor
            label="Capabilities (what the system does)"
            rows={f.capabilities}
            onAdd={() => addRow("capabilities", { title: "", desc: "" })}
            onRemove={(i) => removeRow("capabilities", i)}
            onUpdate={(i, patch) => updateRow("capabilities", i, patch)}
            fields={[
              { key: "title", label: "Title", placeholder: "Unified WhatsApp inbox" },
              { key: "desc", label: "Description", area: true, placeholder: "Two-way WhatsApp Business threads with AI-suggested replies…" },
            ]}
            testid="pf-caps"
          />
          <RowEditor
            label="Workflow (how it works, step-by-step)"
            rows={f.workflow}
            onAdd={() => addRow("workflow", { title: "", desc: "" })}
            onRemove={(i) => removeRow("workflow", i)}
            onUpdate={(i, patch) => updateRow("workflow", i, patch)}
            fields={[
              { key: "title", label: "Step title" },
              { key: "desc", label: "Description", area: true },
            ]}
            testid="pf-flow"
          />
          <Field label="Technologies (comma-separated — structured)" value={f.technologies_str || ""} onChange={(v) => setF({ ...f, technologies_str: v })} testid="pf-tech-struct" placeholder="React, Next.js, FastAPI, MongoDB" />
          <Field label="Legacy — tech_stack (comma-separated, kept for backward compat)" value={f.tech_stack_str} onChange={(v) => setF({ ...f, tech_stack_str: v })} testid="pf-stack" />
          <FieldArea label="CloudLab's role (one per line)" value={f.cloud_lab_role_str || ""} onChange={(v) => setF({ ...f, cloud_lab_role_str: v })} rows={5} testid="pf-role" placeholder={"Product engineering\nArchitecture\nAI agent integration"} />
        </div>

        {/* ============ EVIDENCE ============ */}
        <div hidden={tab !== "evidence"} className="space-y-5">
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.05] p-4 text-xs" data-testid="pf-metric-governance">
            <div className="font-semibold text-amber-600 mb-1">Evidence governance</div>
            A metric only renders publicly when <b>Approved for public display</b> is ticked. Keep unverified figures as drafts here until the owner reconfirms them — the public case study page will simply hide them.
          </div>
          <MetricsEditor
            metrics={f.metrics}
            onAdd={() => addRow("metrics", {
              id: `m-${Date.now().toString(36)}`,
              label: "", value: "", before: "", after: "",
              qualifier: "", context: "", kind: "outcome",
              source: "", last_updated: new Date().toISOString().slice(0, 10),
              approved_for_public_display: false,
              relevant_for: [],
            })}
            onRemove={(i) => removeRow("metrics", i)}
            onUpdate={(i, patch) => updateRow("metrics", i, patch)}
            serviceOptions={SERVICE_OPTIONS}
          />
          <RowEditor
            label="Testimonials (verified-only policy — approve explicitly)"
            rows={f.testimonials}
            onAdd={() => addRow("testimonials", {
              quote: "", person_name: "", role: "", company: "",
              source: "", verified: false, approved_for_public_display: false,
            })}
            onRemove={(i) => removeRow("testimonials", i)}
            onUpdate={(i, patch) => updateRow("testimonials", i, patch)}
            fields={[
              { key: "quote", label: "Quote", area: true },
              { key: "person_name", label: "Person" },
              { key: "role", label: "Role" },
              { key: "company", label: "Company" },
              { key: "source", label: "Source note (internal)" },
              { key: "verified", label: "Verified", checkbox: true },
              { key: "approved_for_public_display", label: "Approved for public display", checkbox: true },
            ]}
            testid="pf-testimonials"
          />
          {validation && (
            <ValidationReport v={validation} />
          )}
        </div>

        {/* ============ SEO ============ */}
        <div hidden={tab !== "seo"} className="space-y-4">
          <Field label="SEO title" value={f.seo?.title || ""} onChange={(v) => setF({ ...f, seo: { ...f.seo, title: v } })} testid="pf-seo-title" placeholder={`${f.name || "Project"} · Case Study`} />
          <FieldArea label="Meta description" value={f.seo?.description || ""} onChange={(v) => setF({ ...f, seo: { ...f.seo, description: v } })} rows={3} testid="pf-seo-desc" />
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Canonical URL override (rare)" value={f.seo?.canonical_override || ""} onChange={(v) => setF({ ...f, seo: { ...f.seo, canonical_override: v } })} testid="pf-seo-canonical" />
            <Field label="OG image URL" value={f.seo?.og_image || ""} onChange={(v) => setF({ ...f, seo: { ...f.seo, og_image: v } })} testid="pf-seo-og-image" />
            <Field label="OG title" value={f.seo?.og_title || ""} onChange={(v) => setF({ ...f, seo: { ...f.seo, og_title: v } })} testid="pf-seo-og-title" />
            <Field label="OG description" value={f.seo?.og_description || ""} onChange={(v) => setF({ ...f, seo: { ...f.seo, og_description: v } })} testid="pf-seo-og-desc" />
          </div>
          <label className="flex items-center gap-2 text-sm" data-testid="pf-seo-noindex-wrap">
            <input type="checkbox" checked={!!f.seo?.noindex} onChange={(e) => setF({ ...f, seo: { ...f.seo, noindex: e.target.checked } })} data-testid="pf-seo-noindex" />
            noindex (force exclusion even when published)
          </label>
        </div>

        {/* ============ GEO / ARO ============ */}
        <div hidden={tab !== "geo"} className="space-y-4">
          <div className="rounded-xl bg-muted/40 p-4 text-xs text-muted-foreground">
            Structured answer-oriented fields — fed to generative-search engines and used by the public page&apos;s Quick Facts block (future). Use plain, factual sentences only.
          </div>
          <FieldArea label="What was built" value={f.answer_profile?.what_was_built || ""} onChange={(v) => setF({ ...f, answer_profile: { ...f.answer_profile, what_was_built: v } })} rows={2} testid="pf-ap-what" />
          <FieldArea label="Who it was for" value={f.answer_profile?.who_it_was_for || ""} onChange={(v) => setF({ ...f, answer_profile: { ...f.answer_profile, who_it_was_for: v } })} rows={2} testid="pf-ap-who" />
          <FieldArea label="Problem solved" value={f.answer_profile?.problem_solved || ""} onChange={(v) => setF({ ...f, answer_profile: { ...f.answer_profile, problem_solved: v } })} rows={2} testid="pf-ap-problem" />
          <FieldArea label="How it works" value={f.answer_profile?.how_it_works || ""} onChange={(v) => setF({ ...f, answer_profile: { ...f.answer_profile, how_it_works: v } })} rows={2} testid="pf-ap-how" />
          <FieldArea label="Important facts (one per line)" value={(f.answer_profile?.important_facts || []).join("\n")} onChange={(v) => setF({ ...f, answer_profile: { ...f.answer_profile, important_facts: v.split("\n").filter(Boolean) } })} rows={3} testid="pf-ap-facts" />
        </div>

        {/* ============ MEDIA ============ */}
        <div hidden={tab !== "media"} className="space-y-5">
          <div className="grid md:grid-cols-2 gap-6">
            <ImageUploader
              label="Thumbnail image"
              hint="Used on Featured Projects cards & listings. 800 × 1000 px recommended."
              value={f.thumbnail_image || ""}
              onChange={(v) => setF({ ...f, thumbnail_image: v })}
              testid="pf-thumb"
              maxW={1000} maxH={1400} aspect="4 / 5"
            />
            <ImageUploader
              label="Cover / hero image *"
              hint="Used on both the project detail and case-study detail pages. 1920 × 1080 px recommended."
              value={f.cover_image || ""}
              onChange={(v) => setF({ ...f, cover_image: v })}
              testid="pf-cover"
              maxW={1920} maxH={1200} aspect="16 / 9"
            />
          </div>
          <Field label="Alternative hero image URL (optional)" value={f.hero_image || ""} onChange={(v) => setF({ ...f, hero_image: v })} testid="pf-hero" placeholder="defaults to cover image" />
        </div>

        {/* ============ PUBLISHING ============ */}
        <div hidden={tab !== "publishing"} className="space-y-5">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Publication status</label>
              <select
                value={f.publication_status}
                onChange={(e) => setF({ ...f, publication_status: e.target.value })}
                data-testid="pf-pub-status"
                className="mt-2 w-full bg-background border border-border rounded-xl px-4 py-2.5 text-sm"
              >
                {PUB_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 mt-6" data-testid="pf-publish-cs-wrap">
              <input
                type="checkbox"
                checked={!!f.publish_as_case_study}
                onChange={(e) => setF({ ...f, publish_as_case_study: e.target.checked })}
                data-testid="pf-publish-case-study"
              />
              <span className="text-sm">Publish as case study → <code className="text-xs">/case-studies/{f.slug_override?.trim() || f.slug || "{slug}"}</code></span>
            </label>
            <label className="flex items-center gap-2" data-testid="pf-published-wrap">
              <input
                type="checkbox"
                checked={!!f.published}
                onChange={(e) => setF({ ...f, published: e.target.checked })}
                data-testid="pf-published"
              />
              <span className="text-sm">Published on <code className="text-xs">/projects/{f.slug || "{slug}"}</code> (legacy)</span>
            </label>
          </div>

          {/* Cross-publish to Portfolio */}
          <div className="rounded-2xl border border-primary/20 bg-primary/[0.04] p-4 md:p-5 space-y-3" data-testid="pf-portfolio-sync">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!f.publish_to_portfolio}
                onChange={(e) => setF({ ...f, publish_to_portfolio: e.target.checked })}
                data-testid="pf-publish-to-portfolio"
                className="mt-1"
              />
              <span>
                <span className="font-medium text-sm">Also publish to Portfolio</span>
                <span className="block text-xs text-muted-foreground mt-0.5">
                  {f.linked_portfolio_slug
                    ? "Linked portfolio item will be kept in sync on every save."
                    : "A matching item will be auto-created on /portfolio so you don't have to enter it twice."}
                </span>
              </span>
            </label>
            {f.publish_to_portfolio && (
              <div>
                <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Portfolio category</label>
                <select
                  value={f.portfolio_category_slug || ""}
                  onChange={(e) => setF({ ...f, portfolio_category_slug: e.target.value })}
                  data-testid="pf-portfolio-category"
                  className="mt-2 w-full bg-background border border-border rounded-xl px-4 py-2.5 text-sm"
                >
                  <option value="">Auto (Web Applications or first available)</option>
                  {portfolioCategories.map((c) => (
                    <option key={c.id} value={c.slug}>{c.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {validation && <ValidationReport v={validation} />}
          <div className="flex flex-wrap gap-2 items-center">
            <button type="button" onClick={runValidate} disabled={validating} className="rounded-full px-4 py-2 border border-border text-sm inline-flex items-center gap-2" data-testid="pf-validate">
              {validating ? "Validating…" : "Validate"}
            </button>
            {previewHref && (
              <a href={previewHref} target="_blank" rel="noreferrer" className="rounded-full px-4 py-2 border border-border text-sm inline-flex items-center gap-2" data-testid="pf-preview">
                Preview in new tab
              </a>
            )}
            <span className="text-xs text-muted-foreground">Preview uses the current admin session.</span>
          </div>
        </div>

        {/* Sticky footer */}
        <div className="flex flex-wrap gap-3 pt-4 border-t border-border -mx-6 px-6 sticky bottom-0 bg-background">
          <button type="submit" className="rounded-full px-5 py-2.5 bg-primary text-primary-foreground text-sm font-medium inline-flex items-center gap-2" data-testid="pf-submit">
            <Save className="w-4 h-4" /> Save
          </button>
          <button type="button" onClick={onCancel} className="rounded-full px-5 py-2.5 border border-border text-sm" data-testid="pf-cancel">Cancel</button>
        </div>
      </form>
    </Modal>
  );
}

function StatusPill({ ok, label }) {
  return (
    <span className={`px-2 py-1 rounded-full font-mono uppercase tracking-wider border ${
      ok ? "border-emerald-500/40 text-emerald-500 bg-emerald-500/10"
         : "border-amber-500/40 text-amber-600 bg-amber-500/10"
    }`}>{ok ? "✓" : "⚠"} {label}</span>
  );
}

function ChecklistField({ label, hint, options, value = [], onToggle, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
      {hint && <p className="mt-1 text-xs text-muted-foreground/80">{hint}</p>}
      <div className="mt-2.5 flex flex-wrap gap-2">
        {options.map((o) => {
          const checked = value.includes(o.value);
          return (
            <button
              type="button"
              key={o.value}
              onClick={() => onToggle(o.value)}
              data-testid={`${testid}-${o.value}`}
              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                checked ? "bg-primary text-primary-foreground border-primary"
                        : "border-border text-foreground/80 hover:border-primary/40"
              }`}
            >{o.label}</button>
          );
        })}
      </div>
    </div>
  );
}

function RowEditor({ label, rows = [], fields, onAdd, onRemove, onUpdate, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
        <button type="button" onClick={onAdd} className="text-xs rounded-full px-3 py-1 border border-border inline-flex items-center gap-1" data-testid={`${testid}-add`}>
          <Plus className="w-3 h-3" /> Add
        </button>
      </div>
      <div className="space-y-3">
        {rows.length === 0 && <div className="text-xs text-muted-foreground italic">No rows yet.</div>}
        {rows.map((row, i) => (
          <div key={i} className="rounded-xl border border-border bg-card p-3 space-y-2 relative" data-testid={`${testid}-row-${i}`}>
            {fields.map((fd) => {
              if (fd.checkbox) {
                return (
                  <label key={fd.key} className="flex items-center gap-2 text-xs">
                    <input type="checkbox" checked={!!row[fd.key]} onChange={(e) => onUpdate(i, { [fd.key]: e.target.checked })} data-testid={`${testid}-row-${i}-${fd.key}`} />
                    {fd.label}
                  </label>
                );
              }
              if (fd.area) {
                return (
                  <div key={fd.key}>
                    <label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{fd.label}</label>
                    <textarea
                      value={row[fd.key] || ""}
                      onChange={(e) => onUpdate(i, { [fd.key]: e.target.value })}
                      rows={2}
                      placeholder={fd.placeholder}
                      data-testid={`${testid}-row-${i}-${fd.key}`}
                      className="mt-1 w-full bg-background border border-border rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                );
              }
              return (
                <div key={fd.key}>
                  <label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{fd.label}</label>
                  <input
                    value={row[fd.key] || ""}
                    onChange={(e) => onUpdate(i, { [fd.key]: e.target.value })}
                    placeholder={fd.placeholder}
                    data-testid={`${testid}-row-${i}-${fd.key}`}
                    className="mt-1 w-full bg-background border border-border rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              );
            })}
            <button type="button" onClick={() => onRemove(i)} className="absolute top-2 right-2 text-xs text-destructive hover:underline" data-testid={`${testid}-row-${i}-remove`}>Remove</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function MetricsEditor({ metrics = [], onAdd, onRemove, onUpdate, serviceOptions }) {
  return (
    <div data-testid="pf-metrics">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Metrics</div>
        <button type="button" onClick={onAdd} className="text-xs rounded-full px-3 py-1 border border-border inline-flex items-center gap-1" data-testid="pf-metrics-add">
          <Plus className="w-3 h-3" /> Add metric
        </button>
      </div>
      <div className="space-y-3">
        {metrics.length === 0 && <div className="text-xs text-muted-foreground italic">No metrics yet.</div>}
        {metrics.map((m, i) => (
          <div key={i} className="rounded-xl border border-border bg-card p-3 space-y-2" data-testid={`pf-metric-${i}`}>
            <div className="grid md:grid-cols-2 gap-2">
              <SmallField label="Label" value={m.label || ""} onChange={(v) => onUpdate(i, { label: v })} testid={`pf-metric-${i}-label`} />
              <div>
                <label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Kind</label>
                <select value={m.kind || "outcome"} onChange={(e) => onUpdate(i, { kind: e.target.value })} data-testid={`pf-metric-${i}-kind`} className="mt-1 w-full bg-background border border-border rounded-lg px-3 py-2 text-sm">
                  <option value="outcome">outcome</option>
                  <option value="capability">capability</option>
                </select>
              </div>
              <SmallField label="Before (optional)" value={m.before || ""} onChange={(v) => onUpdate(i, { before: v })} testid={`pf-metric-${i}-before`} />
              <SmallField label="After (optional)" value={m.after || ""} onChange={(v) => onUpdate(i, { after: v })} testid={`pf-metric-${i}-after`} />
              <SmallField label="Single value (if no before/after)" value={m.value || ""} onChange={(v) => onUpdate(i, { value: v })} testid={`pf-metric-${i}-value`} />
              <SmallField label="Qualifier" value={m.qualifier || ""} onChange={(v) => onUpdate(i, { qualifier: v })} testid={`pf-metric-${i}-qualifier`} placeholder="pilot · approximately" />
            </div>
            <SmallArea label="Context" value={m.context || ""} onChange={(v) => onUpdate(i, { context: v })} testid={`pf-metric-${i}-context`} />
            <div className="grid md:grid-cols-2 gap-2">
              <SmallField label="Source (internal, never public)" value={m.source || ""} onChange={(v) => onUpdate(i, { source: v })} testid={`pf-metric-${i}-source`} />
              <SmallField label="Last updated" type="date" value={m.last_updated || ""} onChange={(v) => onUpdate(i, { last_updated: v })} testid={`pf-metric-${i}-updated`} />
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1">Relevant for services</div>
              <div className="flex flex-wrap gap-1.5">
                {serviceOptions.map((s) => {
                  const on = (m.relevant_for || []).includes(s.slug);
                  return (
                    <button key={s.slug} type="button"
                      onClick={() => {
                        const rf = new Set(m.relevant_for || []);
                        if (on) rf.delete(s.slug); else rf.add(s.slug);
                        onUpdate(i, { relevant_for: [...rf] });
                      }}
                      className={`px-2 py-1 rounded-full text-[10px] border ${on ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground"}`}
                      data-testid={`pf-metric-${i}-rf-${s.slug}`}
                    >{s.name}</button>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center justify-between pt-1">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!m.approved_for_public_display} onChange={(e) => onUpdate(i, { approved_for_public_display: e.target.checked })} data-testid={`pf-metric-${i}-approved`} />
                <span className={m.approved_for_public_display ? "text-emerald-500 font-medium" : ""}>Approved for public display</span>
              </label>
              <button type="button" onClick={() => onRemove(i)} className="text-xs text-destructive hover:underline" data-testid={`pf-metric-${i}-remove`}>Remove</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SmallField({ label, value, onChange, type = "text", testid, placeholder }) {
  return (
    <div>
      <label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder} className="mt-1 w-full bg-background border border-border rounded-lg px-3 py-2 text-sm" />
    </div>
  );
}

function SmallArea({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{label}</label>
      <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={2} data-testid={testid} className="mt-1 w-full bg-background border border-border rounded-lg px-3 py-2 text-sm" />
    </div>
  );
}

function ValidationReport({ v }) {
  const { errors = [], warnings = [], info = [], ready_to_publish } = v;
  return (
    <div className="rounded-xl border border-border bg-card p-4 text-xs space-y-2" data-testid="pf-validation">
      <div className={`font-mono uppercase tracking-widest ${ready_to_publish ? "text-emerald-500" : "text-amber-600"}`}>
        {ready_to_publish ? "✓ Ready to publish" : `⚠ ${errors.length} error · ${warnings.length} warning`}
      </div>
      {errors.length > 0 && (
        <div><div className="font-semibold text-destructive mb-1">Errors</div><ul className="list-disc pl-5 space-y-0.5">{errors.map((e, i) => <li key={i} data-testid={`pf-val-error-${i}`}>{e}</li>)}</ul></div>
      )}
      {warnings.length > 0 && (
        <div><div className="font-semibold text-amber-600 mb-1">Warnings</div><ul className="list-disc pl-5 space-y-0.5">{warnings.map((e, i) => <li key={i} data-testid={`pf-val-warn-${i}`}>{e}</li>)}</ul></div>
      )}
      {info.length > 0 && (
        <div><div className="font-semibold text-muted-foreground mb-1">Info</div><ul className="list-disc pl-5 space-y-0.5">{info.map((e, i) => <li key={i} data-testid={`pf-val-info-${i}`}>{e}</li>)}</ul></div>
      )}
    </div>
  );
}

// ---- Job Editor Modal ----
function JobEditor({ initial, onCancel, onSave }) {
  const [f, setF] = useState({
    ...initial,
    requirements_str: (initial.requirements || []).join("\n"),
    benefits_str: (initial.benefits || []).join("\n"),
  });

  const submit = (e) => {
    e.preventDefault();
    const payload = {
      ...(f.id ? { id: f.id } : {}),
      title: f.title, department: f.department, location: f.location, type: f.type, remote: !!f.remote,
      summary: f.summary, description: f.description,
      requirements: f.requirements_str.split("\n").map((s) => s.trim()).filter(Boolean),
      benefits: f.benefits_str.split("\n").map((s) => s.trim()).filter(Boolean),
      published: !!f.published,
    };
    onSave(payload);
  };

  return (
    <Modal onCancel={onCancel} title={f.id ? "Edit job" : "New job"} testid="job-editor">
      <form onSubmit={submit} className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Title *" required value={f.title} onChange={(v) => setF({ ...f, title: v })} testid="jf-title" />
          <Field label="Department *" required value={f.department} onChange={(v) => setF({ ...f, department: v })} testid="jf-department" />
          <Field label="Location *" required value={f.location} onChange={(v) => setF({ ...f, location: v })} testid="jf-location" placeholder="London, UK" />
          <div>
            <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Type</label>
            <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className="mt-2 w-full bg-background border border-border rounded-xl px-4 py-3" data-testid="jf-type">
              {["Full-time","Part-time","Contract","Internship"].map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <label className="flex items-center gap-2"><input type="checkbox" checked={!!f.remote} onChange={(e) => setF({ ...f, remote: e.target.checked })} data-testid="jf-remote" /> <span className="text-sm">Remote-friendly</span></label>
        <Field label="Summary *" required value={f.summary} onChange={(v) => setF({ ...f, summary: v })} testid="jf-summary" />
        <FieldArea label="Description *" required value={f.description} onChange={(v) => setF({ ...f, description: v })} rows={5} testid="jf-description" />
        <FieldArea label="Requirements (one per line)" value={f.requirements_str} onChange={(v) => setF({ ...f, requirements_str: v })} rows={5} testid="jf-requirements" />
        <FieldArea label="Benefits (one per line)" value={f.benefits_str} onChange={(v) => setF({ ...f, benefits_str: v })} rows={4} testid="jf-benefits" />
        <label className="flex items-center gap-2"><input type="checkbox" checked={!!f.published} onChange={(e) => setF({ ...f, published: e.target.checked })} data-testid="jf-published" /> <span className="text-sm">Published (visible on /careers)</span></label>
        <div className="flex gap-3 pt-2">
          <button type="submit" className="rounded-full px-5 py-2.5 bg-primary text-primary-foreground text-sm font-medium inline-flex items-center gap-2" data-testid="jf-submit"><Save className="w-4 h-4" /> Save</button>
          <button type="button" onClick={onCancel} className="rounded-full px-5 py-2.5 border border-border text-sm">Cancel</button>
        </div>
      </form>
    </Modal>
  );
}

// ---- News Editor Modal ----
function NewsEditor({ initial, onCancel, onSave }) {
  const [f, setF] = useState({ ...initial });

  const submit = (e) => {
    e.preventDefault();
    onSave({
      ...(f.id ? { id: f.id } : {}),
      title: f.title, description: f.description, image: f.image,
      link: f.link || null, tag: f.tag || null,
      order: Number(f.order) || 100, published: !!f.published,
    });
  };

  return (
    <Modal onCancel={onCancel} title={f.id ? "Edit news item" : "New news item"} testid="news-editor">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Title *" required value={f.title} onChange={(v) => setF({ ...f, title: v })} testid="nf-title" />
        <FieldArea label="Description *" required value={f.description} onChange={(v) => setF({ ...f, description: v })} rows={4} testid="nf-description" />
        <ImageUploader
          label="Image *"
          hint="Shown in the What's Latest slider. Recommended 1600 × 900 px (landscape, 16 : 9). Auto-optimized."
          value={f.image || ""}
          onChange={(v) => setF({ ...f, image: v })}
          testid="nf-image"
          maxW={1600}
          maxH={1000}
          aspect="16 / 9"
        />
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Tag" value={f.tag || ""} onChange={(v) => setF({ ...f, tag: v })} testid="nf-tag" placeholder="Launch, Award, Milestone, Partnership" />
          <Field label="External link (optional)" value={f.link || ""} onChange={(v) => setF({ ...f, link: v })} testid="nf-link" />
          <Field label="Sort order" type="number" value={String(f.order)} onChange={(v) => setF({ ...f, order: v })} testid="nf-order" />
          <label className="flex items-center gap-2 mt-6"><input type="checkbox" checked={!!f.published} onChange={(e) => setF({ ...f, published: e.target.checked })} data-testid="nf-published" /> <span className="text-sm">Published</span></label>
        </div>
        <div className="flex gap-3 pt-2">
          <button type="submit" className="rounded-full px-5 py-2.5 bg-primary text-primary-foreground text-sm font-medium inline-flex items-center gap-2" data-testid="nf-submit"><Save className="w-4 h-4" /> Save</button>
          <button type="button" onClick={onCancel} className="rounded-full px-5 py-2.5 border border-border text-sm">Cancel</button>
        </div>
      </form>
    </Modal>
  );
}


// ---- Client Editor Modal ----
// Resize any image blob/URL to fit within LOGO_W x LOGO_H (transparent-aware PNG)
// then return a base64 data URL that we store directly in `logo_url`.
// We store at 3× the on-screen size (76×67) so the logo stays crisp even on
// retina displays and when it scales up on hover.
const LOGO_W = 228;
const LOGO_H = 201;
async function resizeImageToLogo(fileOrBlob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("read failed"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("image decode failed"));
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = LOGO_W;
        canvas.height = LOGO_H;
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, LOGO_W, LOGO_H);
        // contain-fit into 76x67, preserving aspect ratio, centered
        const ratio = Math.min(LOGO_W / img.width, LOGO_H / img.height);
        const w = img.width * ratio;
        const h = img.height * ratio;
        const x = (LOGO_W - w) / 2;
        const y = (LOGO_H - h) / 2;
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(img, x, y, w, h);
        resolve(canvas.toDataURL("image/png"));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(fileOrBlob);
  });
}

function ClientEditor({ initial, onCancel, onSave }) {
  const [f, setF] = useState({ ...initial });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file || !file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await resizeImageToLogo(file);
      setF((prev) => ({ ...prev, logo_url: dataUrl }));
      toast.success("Logo optimized for the marquee");
    } catch {
      toast.error("Could not process image.");
    } finally {
      setUploading(false);
    }
  };

  const onPaste = async (e) => {
    const items = e.clipboardData?.items || [];
    for (const it of items) {
      if (it.type && it.type.startsWith("image/")) {
        const blob = it.getAsFile();
        if (blob) {
          e.preventDefault();
          await handleFile(blob);
          return;
        }
      }
    }
  };

  const onDrop = async (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file) await handleFile(file);
  };

  const submit = (e) => {
    e.preventDefault();
    onSave({
      ...(f.id ? { id: f.id } : {}),
      name: f.name,
      logo_url: f.logo_url || null,
      domain: f.domain || null,
      website: f.website || null,
      order: Number(f.order) || 100,
      published: !!f.published,
    });
  };

  const previewSrc = f.logo_url || (f.domain ? `https://logo.clearbit.com/${f.domain}?size=200` : null);

  return (
    <Modal onCancel={onCancel} title={f.id ? "Edit client" : "Add client"} testid="client-editor">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Client name *" required value={f.name} onChange={(v) => setF({ ...f, name: v })} testid="cf-name" />

        {/* Upload / paste zone */}
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2">
            Upload logo (auto-resized to 76 × 67)
          </div>
          <div
            onDrop={onDrop}
            onDragOver={(e) => e.preventDefault()}
            onPaste={onPaste}
            tabIndex={0}
            data-testid="cf-upload-zone"
            className="rounded-xl border-2 border-dashed border-border bg-muted/20 p-5 text-center outline-none focus:border-primary/60 transition-colors"
          >
            <div className="flex flex-wrap items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                data-testid="cf-browse-btn"
                className="rounded-full px-4 py-2 bg-primary text-primary-foreground text-sm font-medium"
              >
                {uploading ? "Processing…" : "Browse & upload"}
              </button>
              <span className="text-xs text-muted-foreground">
                or <strong>drag & drop</strong> · or click here and press <kbd className="px-1.5 py-0.5 rounded bg-foreground/10 text-[10px] font-mono">Ctrl / ⌘ + V</kbd> to paste
              </span>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              data-testid="cf-file-input"
              onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
            />
          </div>
        </div>

        <Field label="Or paste image URL" value={f.logo_url && f.logo_url.startsWith("data:") ? "" : (f.logo_url || "")} onChange={(v) => setF({ ...f, logo_url: v })} testid="cf-logo" placeholder="https://.../logo.png or .svg" />
        <Field label="Or brand domain (Clearbit auto-fetches the logo)" value={f.domain || ""} onChange={(v) => setF({ ...f, domain: v })} testid="cf-domain" placeholder="pizzahut.com" />
        <Field label="Website (optional)" value={f.website || ""} onChange={(v) => setF({ ...f, website: v })} testid="cf-website" placeholder="https://www.client.com" />

        {previewSrc && (
          <div className="p-4 rounded-xl border border-border bg-muted/30 flex items-center gap-4">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2">Preview (76 × 67)</div>
              <div
                className="bg-white rounded-md border border-border flex items-center justify-center"
                style={{ width: LOGO_W, height: LOGO_H }}
              >
                <img
                  src={previewSrc}
                  alt="preview"
                  style={{ width: LOGO_W, height: LOGO_H, objectFit: "contain" }}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  data-testid="cf-preview-img"
                />
              </div>
            </div>
            {f.logo_url && (
              <button
                type="button"
                onClick={() => setF({ ...f, logo_url: "" })}
                data-testid="cf-clear-logo"
                className="text-xs text-muted-foreground hover:text-destructive underline"
              >
                Remove logo
              </button>
            )}
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Sort order" type="number" value={String(f.order)} onChange={(v) => setF({ ...f, order: v })} testid="cf-order" />
          <label className="flex items-center gap-2 mt-6"><input type="checkbox" checked={!!f.published} onChange={(e) => setF({ ...f, published: e.target.checked })} data-testid="cf-published" /> <span className="text-sm">Published</span></label>
        </div>
        <div className="flex gap-3 pt-2">
          <button type="submit" className="rounded-full px-5 py-2.5 bg-primary text-primary-foreground text-sm font-medium inline-flex items-center gap-2" data-testid="cf-submit"><Save className="w-4 h-4" /> Save</button>
          <button type="button" onClick={onCancel} className="rounded-full px-5 py-2.5 border border-border text-sm">Cancel</button>
        </div>
      </form>
    </Modal>
  );
}


function Modal({ children, title, onCancel, testid, wide }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onCancel} data-testid={testid}>
      <div className={`bg-background border border-border rounded-3xl w-full ${wide ? "max-w-5xl" : "max-w-3xl"} my-10 p-6 md:p-8`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-heading font-bold text-2xl">{title}</h3>
          <button onClick={onCancel} className="p-2 rounded-full hover:bg-foreground/10"><X className="w-4 h-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, required, type = "text", placeholder, testid, className = "" }) {
  return (
    <div className={className}>
      <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">{label}</label>
      <input
        type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="mt-2 w-full bg-transparent border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
        data-testid={testid}
      />
    </div>
  );
}

function FieldArea({ label, value, onChange, required, rows = 4, placeholder, testid }) {
  return (
    <div>
      <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">{label}</label>
      <textarea
        required={required} value={value} onChange={(e) => onChange(e.target.value)} rows={rows} placeholder={placeholder}
        className="mt-2 w-full bg-transparent border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 resize-y"
        data-testid={testid}
      />
    </div>
  );
}

// ---- Recognition Editor Modal ----
function RecognitionEditor({ initial, onCancel, onSave }) {
  const [f, setF] = useState({ ...initial });
  const submit = (e) => {
    e.preventDefault();
    onSave({
      ...(f.id ? { id: f.id } : {}),
      title: f.title,
      description: f.description || null,
      image: f.image || null,
      link: f.link || null,
      duration_ms: Number(f.duration_ms) || 5000,
      order: Number(f.order) || 100,
      published: !!f.published,
    });
  };
  return (
    <Modal onCancel={onCancel} title={f.id ? "Edit recognition" : "Add recognition"} testid="recognition-editor">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Title *" required value={f.title} onChange={(v) => setF({ ...f, title: v })} testid="rf-title" placeholder="Winner · Pakistan Software Awards 2024" />
        <FieldArea label="Description (optional)" value={f.description || ""} onChange={(v) => setF({ ...f, description: v })} rows={3} testid="rf-description" />
        <ImageUploader
          label="Badge / logo (optional)"
          hint="Recommended 300 × 300 px (square). Auto-optimized."
          value={f.image || ""}
          onChange={(v) => setF({ ...f, image: v })}
          testid="rf-image"
          maxW={600}
          maxH={600}
          aspect="1 / 1"
        />
        <Field label="Link (optional)" value={f.link || ""} onChange={(v) => setF({ ...f, link: v })} testid="rf-link" placeholder="https://..." />
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Display duration (ms)" type="number" value={String(f.duration_ms)} onChange={(v) => setF({ ...f, duration_ms: v })} testid="rf-duration" />
          <Field label="Sort order" type="number" value={String(f.order)} onChange={(v) => setF({ ...f, order: v })} testid="rf-order" />
          <label className="flex items-center gap-2 mt-6"><input type="checkbox" checked={!!f.published} onChange={(e) => setF({ ...f, published: e.target.checked })} data-testid="rf-published" /> <span className="text-sm">Published</span></label>
        </div>
        <div className="flex gap-3 pt-2">
          <button type="submit" className="rounded-full px-5 py-2.5 bg-primary text-primary-foreground text-sm font-medium inline-flex items-center gap-2" data-testid="rf-submit"><Save className="w-4 h-4" /> Save</button>
          <button type="button" onClick={onCancel} className="rounded-full px-5 py-2.5 border border-border text-sm">Cancel</button>
        </div>
      </form>
    </Modal>
  );
}


function ReviewEditor({ initial, onCancel, onSave }) {
  const [f, setF] = useState({ ...initial });
  const submit = (e) => {
    e.preventDefault();
    onSave({
      ...(f.id ? { id: f.id } : {}),
      author_name: f.author_name,
      author_role: f.author_role || null,
      avatar_url: f.avatar_url || null,
      rating: Math.max(1, Math.min(5, Number(f.rating) || 5)),
      text: f.text,
      source: f.source || "Google",
      source_url: f.source_url || null,
      country: f.country || null,
      order: Number(f.order) || 100,
      published: !!f.published,
    });
  };
  const SOURCES = ["Google", "Clutch", "LinkedIn", "GoodFirms"];
  return (
    <Modal onCancel={onCancel} title={f.id ? "Edit review" : "Add review"} testid="review-editor">
      <form onSubmit={submit} className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Author name *" required value={f.author_name} onChange={(v) => setF({ ...f, author_name: v })} testid="rvf-name" placeholder="Adeel Rahman" />
          <Field label="Role / Company" value={f.author_role || ""} onChange={(v) => setF({ ...f, author_role: v })} testid="rvf-role" placeholder="CTO, Whitmore Realty" />
        </div>
        <FieldArea label="Review text *" required value={f.text} onChange={(v) => setF({ ...f, text: v })} rows={5} testid="rvf-text" placeholder="What the client said in their own words…" />
        <ImageUploader
          label="Avatar (optional)"
          hint="Recommended 200 × 200 px (square). Auto-optimized. Leave empty to fall back to initials monogram."
          value={f.avatar_url || ""}
          onChange={(v) => setF({ ...f, avatar_url: v })}
          testid="rvf-avatar"
          maxW={400}
          maxH={400}
          aspect="1 / 1"
        />
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Source</label>
            <select
              value={f.source || "Google"}
              onChange={(e) => setF({ ...f, source: e.target.value })}
              className="mt-2 w-full bg-background border border-border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
              data-testid="rvf-source"
            >
              {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <Field label="Rating (1–5)" type="number" value={String(f.rating)} onChange={(v) => setF({ ...f, rating: v })} testid="rvf-rating" />
          <Field label="Country" value={f.country || ""} onChange={(v) => setF({ ...f, country: v })} testid="rvf-country" placeholder="United Kingdom" />
        </div>
        <Field label="Review URL (optional)" value={f.source_url || ""} onChange={(v) => setF({ ...f, source_url: v })} testid="rvf-source-url" placeholder="https://g.page/r/…" />
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Sort order" type="number" value={String(f.order)} onChange={(v) => setF({ ...f, order: v })} testid="rvf-order" />
          <label className="flex items-center gap-2 mt-6"><input type="checkbox" checked={!!f.published} onChange={(e) => setF({ ...f, published: e.target.checked })} data-testid="rvf-published" /> <span className="text-sm">Published</span></label>
        </div>
        <div className="flex gap-3 pt-2">
          <button type="submit" className="rounded-full px-5 py-2.5 bg-primary text-primary-foreground text-sm font-medium inline-flex items-center gap-2" data-testid="rvf-submit"><Save className="w-4 h-4" /> Save</button>
          <button type="button" onClick={onCancel} className="rounded-full px-5 py-2.5 border border-border text-sm">Cancel</button>
        </div>
      </form>
    </Modal>
  );
}

function ShowcaseVideoEditor({ initial, portfolioCategories = [], onCancel, onSave }) {
  const [f, setF] = useState({
    ...initial,
    publish_to_portfolio: !!initial.linked_portfolio_slug,
    portfolio_category_slug: initial.portfolio_category_slug || "",
    portfolio_media_format: initial.portfolio_media_format || "landscape",
  });
  const urlWarning = (() => {
    const u = (f.video_url || "").trim();
    if (!u) return "";
    const ok = /(?:iframe|player)\.mediadelivery\.net\/(embed|play)\/\d+\/[a-f0-9-]{20,}/i.test(u)
      || /youtube\.com\/embed\/[\w-]{6,}/.test(u)
      || /player\.vimeo\.com\/video\/\d+/.test(u);
    if (ok) return "";
    if (/vz-[a-z0-9]+\.b-cdn\.net/i.test(u)) return "⚠️ This looks like a Bunny CDN direct playback URL. Please paste the iframe embed URL instead — it starts with 'https://iframe.mediadelivery.net/embed/…'.";
    if (u.includes("youtube.com/watch") || u.includes("youtu.be/")) return "⚠️ Use the YouTube embed URL: replace 'watch?v=' with 'embed/' — e.g. https://www.youtube.com/embed/VIDEO_ID.";
    if (u.includes(".m3u8") || u.includes(".mp4")) return "⚠️ Direct media file URLs don't work here — please paste the iframe embed URL from Bunny/YouTube/Vimeo.";
    return "⚠️ This doesn't look like a standard Bunny / YouTube / Vimeo iframe URL. If Bunny shows 'media could not be loaded', add your site domain to the Bunny library's Allowed Referrers.";
  })();
  const submit = (e) => {
    e.preventDefault();
    onSave({
      ...(f.id ? { id: f.id } : {}),
      title: f.title,
      subtitle: f.subtitle || null,
      video_url: f.video_url,
      thumbnail_url: f.thumbnail_url || null,
      duration: f.duration || null,
      tag: f.tag || null,
      project_link: f.project_link || null,
      order: Number(f.order) || 100,
      published: !!f.published,
      publish_to_portfolio: !!f.publish_to_portfolio,
      portfolio_category_slug: f.publish_to_portfolio ? (f.portfolio_category_slug || null) : null,
      portfolio_media_format: f.publish_to_portfolio ? (f.portfolio_media_format || "landscape") : null,
    });
  };
  return (
    <Modal onCancel={onCancel} title={f.id ? "Edit showcase video" : "Add showcase video"} testid="showcase-video-editor">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Title *" required value={f.title} onChange={(v) => setF({ ...f, title: v })} testid="svf-title" placeholder="HelloSir.ai · AI Voice Agent Demo" />
        <FieldArea label="Subtitle / description" value={f.subtitle || ""} onChange={(v) => setF({ ...f, subtitle: v })} rows={2} testid="svf-subtitle" placeholder="One-line description shown under the title on the player" />
        <Field label="Bunny Stream iframe URL *" required value={f.video_url} onChange={(v) => setF({ ...f, video_url: v })} testid="svf-url" placeholder="https://iframe.mediadelivery.net/embed/…" />
        <div className="text-[11px] text-muted-foreground -mt-2">
          Copy the iframe SRC from your Bunny Stream video → Embed. YouTube and Vimeo embed URLs also work.
        </div>
        {urlWarning && (
          <div className="rounded-xl border border-amber-400/40 bg-amber-400/10 text-amber-800 dark:text-amber-200 px-3 py-2 text-[12px] leading-relaxed" data-testid="svf-url-warning">
            {urlWarning}
          </div>
        )}
        <ImageUploader
          label="Custom thumbnail (optional)"
          hint="Recommended 1280 × 720 px (16:9). Leave empty to auto-fetch from Bunny/YouTube."
          value={f.thumbnail_url || ""}
          onChange={(v) => setF({ ...f, thumbnail_url: v })}
          testid="svf-thumb"
          maxW={1600}
          maxH={900}
          aspect="16 / 9"
        />
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Tag" value={f.tag || ""} onChange={(v) => setF({ ...f, tag: v })} testid="svf-tag" placeholder="AI Voice" />
          <Field label="Duration" value={f.duration || ""} onChange={(v) => setF({ ...f, duration: v })} testid="svf-duration" placeholder="1:52" />
          <Field label="Sort order" type="number" value={String(f.order)} onChange={(v) => setF({ ...f, order: v })} testid="svf-order" />
        </div>
        <Field label="Project link (optional)" value={f.project_link || ""} onChange={(v) => setF({ ...f, project_link: v })} testid="svf-link" placeholder="/projects/whitmore-realty" />
        <label className="flex items-center gap-2"><input type="checkbox" checked={!!f.published} onChange={(e) => setF({ ...f, published: e.target.checked })} data-testid="svf-published" /> <span className="text-sm">Published</span></label>

        {/* ---- Cross-publish to Portfolio ---- */}
        <div className="rounded-2xl border border-primary/20 bg-primary/[0.04] p-4 md:p-5 space-y-3" data-testid="svf-portfolio-sync">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={!!f.publish_to_portfolio}
              onChange={(e) => setF({ ...f, publish_to_portfolio: e.target.checked })}
              data-testid="svf-publish-to-portfolio"
              className="mt-1"
            />
            <span>
              <span className="font-medium text-sm">Also publish to Portfolio</span>
              <span className="block text-xs text-muted-foreground mt-0.5">
                {f.linked_portfolio_slug
                  ? "Linked portfolio item will be kept in sync on every save."
                  : "A matching reel will be auto-created on /portfolio so you don't have to enter it twice."}
              </span>
            </span>
          </label>
          {f.publish_to_portfolio && (
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Portfolio category</label>
                <select
                  value={f.portfolio_category_slug || ""}
                  onChange={(e) => setF({ ...f, portfolio_category_slug: e.target.value })}
                  data-testid="svf-portfolio-category"
                  className="mt-2 w-full bg-background border border-border rounded-xl px-4 py-2.5 text-sm"
                >
                  <option value="">Auto (Social Media Productions or first available)</option>
                  {portfolioCategories.map((c) => (
                    <option key={c.id} value={c.slug}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Media format</label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setF({ ...f, portfolio_media_format: "landscape" })}
                    data-testid="svf-format-landscape"
                    className={`rounded-xl px-3 py-2 text-xs border transition-colors ${f.portfolio_media_format === "landscape" ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:border-primary/40"}`}
                  >
                    Landscape 16:9
                  </button>
                  <button
                    type="button"
                    onClick={() => setF({ ...f, portfolio_media_format: "portrait" })}
                    data-testid="svf-format-portrait"
                    className={`rounded-xl px-3 py-2 text-xs border transition-colors ${f.portfolio_media_format === "portrait" ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:border-primary/40"}`}
                  >
                    Portrait 9:16 (phone reel)
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-3 pt-2">
          <button type="submit" className="rounded-full px-5 py-2.5 bg-primary text-primary-foreground text-sm font-medium inline-flex items-center gap-2" data-testid="svf-submit"><Save className="w-4 h-4" /> Save</button>
          <button type="button" onClick={onCancel} className="rounded-full px-5 py-2.5 border border-border text-sm">Cancel</button>
        </div>
      </form>
    </Modal>
  );
}


// ---- Hero Rotator Editor ----
function HeroWordEditor({ initial, onCancel, onSave }) {
  const [f, setF] = useState({ ...initial });
  const submit = (e) => {
    e.preventDefault();
    onSave({
      ...(f.id ? { id: f.id } : {}),
      word: f.word.trim(),
      tagline: (f.tagline || "").trim() || null,
      order: Number(f.order) || 100,
      published: !!f.published,
    });
  };
  return (
    <Modal onCancel={onCancel} title={f.id ? "Edit rotator entry" : "Add rotator entry"} testid="hero-word-editor">
      <form onSubmit={submit} className="space-y-4">
        <Field
          label="Word / product name *"
          required
          value={f.word}
          onChange={(v) => setF({ ...f, word: v })}
          testid="hwf-word"
          placeholder="e.g. Studyportal.ai"
        />
        <FieldArea
          label="Sub-tagline (optional)"
          value={f.tagline || ""}
          onChange={(v) => setF({ ...f, tagline: v })}
          rows={3}
          testid="hwf-tagline"
          placeholder="Short one-liner shown under the H1 while this word is active."
        />
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Sort order" type="number" value={String(f.order)} onChange={(v) => setF({ ...f, order: v })} testid="hwf-order" />
          <label className="flex items-center gap-2 mt-6"><input type="checkbox" checked={!!f.published} onChange={(e) => setF({ ...f, published: e.target.checked })} data-testid="hwf-published" /> <span className="text-sm">Published (shown in rotator)</span></label>
        </div>
        <div className="flex gap-3 pt-2">
          <button type="submit" className="rounded-full px-5 py-2.5 bg-primary text-primary-foreground text-sm font-medium inline-flex items-center gap-2" data-testid="hwf-submit"><Save className="w-4 h-4" /> Save</button>
          <button type="button" onClick={onCancel} className="rounded-full px-5 py-2.5 border border-border text-sm">Cancel</button>
        </div>
      </form>
    </Modal>
  );
}


// ---- Header Nav Item Editor ----
function NavItemEditor({ initial, onCancel, onSave }) {
  const [f, setF] = useState({ ...initial });
  const isChild = !!f.parent_id;
  const submit = (e) => {
    e.preventDefault();
    const path = (f.path || "").trim();
    if (!path) return;
    onSave({
      ...(f.id ? { id: f.id } : {}),
      label: f.label.trim(),
      path,
      order: Number(f.order) || 100,
      published: !!f.published,
      parent_id: f.parent_id || null,
      tagline: (f.tagline || "").trim() || null,
    });
  };
  return (
    <Modal onCancel={onCancel} title={f.id ? `Edit ${isChild ? "submenu item" : "nav item"}` : `Add ${isChild ? "submenu item" : "top-level nav item"}`} testid="nav-item-editor">
      <form onSubmit={submit} className="space-y-4">
        <Field
          label="Label *"
          required
          value={f.label}
          onChange={(v) => setF({ ...f, label: v })}
          testid="nif-label"
          placeholder={isChild ? "e.g. Cloud Infrastructure" : "e.g. Blog"}
        />
        <Field
          label="Path / URL *"
          required
          value={f.path}
          onChange={(v) => setF({ ...f, path: v })}
          testid="nif-path"
          placeholder={isChild ? "/services/cloud-devops" : "/blog  or  https://…"}
        />
        <p className="text-[11px] text-muted-foreground -mt-2">
          Internal routes start with <code className="bg-muted px-1 rounded">/</code>. Absolute <code className="bg-muted px-1 rounded">https://</code> URLs open in a new tab.
        </p>
        {isChild && (
          <FieldArea
            label="Tagline (shown under label in dropdown)"
            value={f.tagline || ""}
            onChange={(v) => setF({ ...f, tagline: v })}
            rows={2}
            testid="nif-tagline"
            placeholder="e.g. Battle-tested cloud, IaC and CI/CD that keeps you shipping every day."
          />
        )}
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Sort order" type="number" value={String(f.order)} onChange={(v) => setF({ ...f, order: v })} testid="nif-order" />
          <label className="flex items-center gap-2 mt-6"><input type="checkbox" checked={!!f.published} onChange={(e) => setF({ ...f, published: e.target.checked })} data-testid="nif-published" /> <span className="text-sm">Published (shown in header)</span></label>
        </div>
        <div className="flex gap-3 pt-2">
          <button type="submit" className="rounded-full px-5 py-2.5 bg-primary text-primary-foreground text-sm font-medium inline-flex items-center gap-2" data-testid="nif-submit"><Save className="w-4 h-4" /> Save</button>
          <button type="button" onClick={onCancel} className="rounded-full px-5 py-2.5 border border-border text-sm">Cancel</button>
        </div>
      </form>
    </Modal>
  );
}


// ---- Submenu Manager: nested table for one parent nav item's children ----
function SubmenuManager({ parent, items, onClose, onEditItem, onToggle, onDelete }) {
  const sorted = [...items].sort((a, b) => (a.order || 0) - (b.order || 0));
  return (
    <Modal onCancel={onClose} title={`Submenus of "${parent.label}"`} testid="nav-submenu-manager" wide>
      <div className="space-y-4">
        <div className="flex justify-between items-center gap-3 flex-wrap">
          <p className="text-sm text-muted-foreground">
            These entries appear inside the <span className="italic">{parent.label}</span> dropdown on the site header. Each may have its own tagline shown under the label.
          </p>
          <button
            onClick={() => onEditItem(null)}
            className="rounded-full px-4 py-2 bg-primary text-primary-foreground text-sm inline-flex items-center gap-2"
            data-testid="admin-submenu-new"
          >
            <Plus className="w-4 h-4" /> Add submenu item
          </button>
        </div>
        <div className="rounded-2xl border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-left text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground bg-foreground/5">
              <tr>
                <th className="p-4">Label</th>
                <th className="p-4">Path</th>
                <th className="p-4">Tagline</th>
                <th className="p-4">Order</th>
                <th className="p-4">Status</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No submenu items yet.</td></tr>}
              {sorted.map((c) => (
                <tr key={c.id} className="border-t border-border hover:bg-foreground/5" data-testid={`admin-submenu-row-${c.id}`}>
                  <td className="p-4 font-medium">{c.label}</td>
                  <td className="p-4 text-xs font-mono text-muted-foreground break-all">{c.path}</td>
                  <td className="p-4 text-xs text-muted-foreground line-clamp-2 max-w-[280px]">{c.tagline || "—"}</td>
                  <td className="p-4">{c.order}</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-mono ${c.published ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                      {c.published ? "Live" : "Hidden"}
                    </span>
                  </td>
                  <td className="p-4 flex gap-2">
                    <button
                      onClick={() => onToggle(c)}
                      className={`p-2 rounded hover:bg-foreground/10 ${c.published ? "" : "text-muted-foreground"}`}
                      title={c.published ? "Hide from dropdown" : "Show in dropdown"}
                      data-testid={`admin-submenu-toggle-${c.id}`}
                    >
                      {c.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                    <button onClick={() => onEditItem(c)} className="p-2 rounded hover:bg-foreground/10" data-testid={`admin-submenu-edit-${c.id}`}>Edit</button>
                    <button onClick={() => onDelete(c.id)} className="p-2 rounded hover:bg-destructive/10 text-destructive"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end pt-1">
          <button type="button" onClick={onClose} className="rounded-full px-5 py-2.5 border border-border text-sm">Close</button>
        </div>
      </div>
    </Modal>
  );
}


// ---- Phase 3.3.1 · Lead source attribution helpers ----
const SERVICE_LABELS = {
  "ai-development": "AI Development",
  "ai-agent-development": "AI Agent Development",
  "custom-software-development": "Custom Software Development",
  "saas-development": "SaaS Development",
  "crm-development": "CRM Development",
  "mobile-app-development": "Mobile App Development",
  "web-application-development": "Web Application Development",
  "edtech-development": "EdTech Development",
  "mvp-development": "MVP Development",
  "software-modernisation": "Software Modernisation",
};
const CASE_STUDY_LABELS = {
  "unicrm-ai": "UniCRM.ai",
  "teachingsoftware-ai": "TeachingSoftware.ai",
  "nexus-one": "Nexus One (draft)",
};
const MARKET_LABELS = {
  uk: "United Kingdom",
  us: "United States",
  pk: "Pakistan",
  ae: "United Arab Emirates",
  sa: "Saudi Arabia",
  de: "Germany",
  europe: "Europe",
};
const SOURCE_TYPE_LABELS = {
  service: "Service page",
  service_page: "Service page",
  case_study: "Case study",
  "case-study": "Case study",
  case_study_hub: "Case study hub",
  market: "Market page",
  homepage: "Homepage",
  about: "About / Trust band",
  about_trust_band: "About / Trust band",
  hero_primary: "Homepage · hero",
  what_building_top: "Homepage · What are you building",
  other: "Other",
  cta: "CTA",
};
const CTA_LOCATION_LABELS = {
  hero: "Hero",
  final: "Final CTA",
  outcomes: "Outcomes CTA",
  "proof-strip": "Proof strip",
  trust_band: "About trust band",
  what_building_top: "What are you building",
};
function labelize(map, slug) {
  if (!slug) return null;
  return map[slug] || String(slug).replace(/[-_]/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
}

// Render the "Lead source" block only when at least one of the Phase 3.3.1
// attribution fields is populated. Older records without any of these fields
// (Phase 3.2 and earlier) render nothing here — the wider Attribution block
// below still renders the legacy landing_page/UTM data.
function LeadSourceSection({ attr }) {
  if (!attr) return null;
  const rows = [
    { label: "Source", value: labelize(SOURCE_TYPE_LABELS, attr.source_type) },
    { label: "Source page", value: attr.source_page, mono: true },
    { label: "Service", value: labelize(SERVICE_LABELS, attr.service_slug) },
    { label: "Case study (direct)", value: labelize(CASE_STUDY_LABELS, attr.case_study_slug) },
    {
      label: "Case-study assisted",
      value:
        attr.assisted_by_case_study
          ? attr.assisted_case_study_slug
            ? `Yes · ${labelize(CASE_STUDY_LABELS, attr.assisted_case_study_slug)}`
            : "Yes"
          : null,
    },
    { label: "CTA", value: labelize(CTA_LOCATION_LABELS, attr.cta_location) },
    { label: "Market", value: labelize(MARKET_LABELS, attr.market_slug) },
  ].filter((r) => r.value);
  if (!rows.length) return null;
  return (
    <div className="rounded-2xl border border-primary/25 bg-primary/[0.04] p-4" data-testid="admin-estimate-lead-source">
      <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-primary">Lead source</div>
      <div className="mt-2 grid md:grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
        {rows.map((r) => (
          <div key={r.label} data-testid={`lead-source-${r.label.replace(/\W+/g, "-").toLowerCase()}`}>
            <span className="text-muted-foreground">{r.label}:</span>{" "}
            <span className={r.mono ? "font-mono break-all" : "font-medium"}>{r.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- Estimate Request Detail Viewer ----
function EstimateDetail({ estimate, onClose }) {
  const e = estimate;
  const attr = e.attribution || {};
  const briefUrl = e.brief_storage_path ? `${API}/estimates/brief/${e.brief_storage_path}` : null;
  const Section = ({ title, children }) => (
    <div>
      <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-muted-foreground">{title}</div>
      <div className="mt-1.5 text-sm">{children}</div>
    </div>
  );
  return (
    <Modal onCancel={onClose} title={`Estimate · ${e.name}`} testid="estimate-detail" wide>
      <div className="space-y-6">
        <div className="grid md:grid-cols-3 gap-4">
          <Section title="Buyer">
            <div className="font-medium">{e.name}</div>
            <div className="text-muted-foreground">{e.company || "—"}</div>
            <div className="mt-1">{e.email}</div>
            {e.phone && <div>{e.phone}</div>}
            {e.country && <div className="text-muted-foreground">{e.country}</div>}
          </Section>
          <Section title="Project fit">
            <div>Type: <span className="font-medium">{e.project_type || "—"}</span></div>
            <div>Stage: {e.stage || "—"}</div>
            <div>Budget: <span className="font-mono">{e.currency || ""} {e.budget_range || "—"}</span></div>
            <div>Timeline: {e.timeline || "—"}</div>
          </Section>
          <Section title="Signals">
            <div>Lead score: <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-mono ${e.lead_score >= 80 ? "bg-primary/15 text-primary" : e.lead_score >= 60 ? "bg-yellow-500/15 text-yellow-600" : "bg-muted"}`}>{e.lead_score}</span></div>
            <div>Status: <span className="font-mono">{e.status}</span></div>
            <div className="text-xs text-muted-foreground mt-1">Received {new Date(e.created_at).toLocaleString()}</div>
          </Section>
        </div>

        {(e.goals?.length || e.capabilities?.length) ? (
          <div className="grid md:grid-cols-2 gap-4">
            <Section title="Goals">
              {(e.goals || []).length ? (
                <div className="flex flex-wrap gap-1.5">
                  {e.goals.map((g) => <span key={g} className="text-xs rounded-full bg-foreground/10 px-2.5 py-1">{g}</span>)}
                </div>
              ) : "—"}
            </Section>
            <Section title="Capabilities">
              {(e.capabilities || []).length ? (
                <div className="flex flex-wrap gap-1.5">
                  {e.capabilities.map((c) => <span key={c} className="text-xs rounded-full bg-foreground/10 px-2.5 py-1">{c}</span>)}
                </div>
              ) : "—"}
            </Section>
          </div>
        ) : null}

        {e.description && (
          <Section title="Description">
            <p className="whitespace-pre-wrap leading-relaxed">{e.description}</p>
          </Section>
        )}

        {briefUrl && (
          <Section title="Brief">
            <a href={briefUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-primary hover:underline text-sm" data-testid="admin-estimate-brief">
              <Download className="w-4 h-4" /> {e.brief_filename || "Download brief"}
            </a>
          </Section>
        )}

        <LeadSourceSection attr={attr} />

        <div className="rounded-2xl border border-border p-4 bg-foreground/[0.02]">
          <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-muted-foreground">Attribution</div>
          <div className="mt-2 grid md:grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
            <div><span className="text-muted-foreground">Landing page:</span> <span className="font-mono break-all">{attr.landing_page || "—"}</span></div>
            <div><span className="text-muted-foreground">Current page:</span> <span className="font-mono break-all">{attr.current_page || "—"}</span></div>
            <div><span className="text-muted-foreground">Referrer:</span> <span className="font-mono break-all">{attr.referrer || "—"}</span></div>
            <div><span className="text-muted-foreground">Device:</span> <span className="font-mono">{attr.device || "—"}</span></div>
            <div><span className="text-muted-foreground">UTM source:</span> <span className="font-mono">{attr.utm_source || "—"}</span></div>
            <div><span className="text-muted-foreground">UTM medium:</span> <span className="font-mono">{attr.utm_medium || "—"}</span></div>
            <div><span className="text-muted-foreground">UTM campaign:</span> <span className="font-mono">{attr.utm_campaign || "—"}</span></div>
            <div><span className="text-muted-foreground">Client host:</span> <span className="font-mono">{attr.client_host || "—"}</span></div>
          </div>
        </div>

        <div className="flex justify-end">
          <button onClick={onClose} className="rounded-full px-5 py-2.5 border border-border text-sm">Close</button>
        </div>
      </div>
    </Modal>
  );
}


// ---- Portfolio Editors ----
function slugify(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 60);
}

function PortfolioCategoryEditor({ initial, onCancel, onSave }) {
  const [f, setF] = useState({ ...initial });
  const submit = (e) => {
    e.preventDefault();
    onSave({
      ...(f.id ? { id: f.id } : {}),
      name: f.name,
      slug: (f.slug && f.slug.trim()) || slugify(f.name),
      order: Number(f.order) || 100,
      published: !!f.published,
    });
  };
  return (
    <Modal onCancel={onCancel} title={f.id ? "Edit category" : "Add portfolio category"} testid="portfolio-cat-editor">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Category name *" required value={f.name} onChange={(v) => setF({ ...f, name: v, slug: f.slug || slugify(v) })} testid="pcf-name" placeholder="Web Applications" />
        <Field label="URL slug *" required value={f.slug} onChange={(v) => setF({ ...f, slug: slugify(v) })} testid="pcf-slug" placeholder="web-applications" />
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Sort order" type="number" value={String(f.order)} onChange={(v) => setF({ ...f, order: v })} testid="pcf-order" />
          <label className="flex items-center gap-2 mt-6"><input type="checkbox" checked={!!f.published} onChange={(e) => setF({ ...f, published: e.target.checked })} data-testid="pcf-published" /> <span className="text-sm">Published</span></label>
        </div>
        <div className="flex gap-3 pt-2">
          <button type="submit" className="rounded-full px-5 py-2.5 bg-primary text-primary-foreground text-sm font-medium inline-flex items-center gap-2" data-testid="pcf-submit"><Save className="w-4 h-4" /> Save</button>
          <button type="button" onClick={onCancel} className="rounded-full px-5 py-2.5 border border-border text-sm">Cancel</button>
        </div>
      </form>
    </Modal>
  );
}

function PortfolioItemEditor({ initial, categories, onCancel, onSave }) {
  const [f, setF] = useState({
    ...initial,
    tech_stack_str: (initial.tech_stack || []).join(", "),
    gallery_str: (initial.gallery || []).join("\n"),
    outcomes_str: (initial.outcomes || []).map((o) => `${o.metric} | ${o.value}`).join("\n"),
    features_str: (initial.features || []).join("\n"),
    process_str: (initial.process || []).map((p) => `${p.step} | ${p.desc}`).join("\n"),
    markets_str: (initial.markets || []).join(", "),
    services_str: (initial.services || []).join(", "),
    industries_str: (initial.industries || []).join(", "),
    media_format: initial.media_format || "landscape",
  });

  const setCategory = (slug) => {
    if (slug === "mobile-applications") setF({ ...f, category_slug: slug, media_format: "portrait" });
    else setF({ ...f, category_slug: slug });
  };

  const submit = (e) => {
    e.preventDefault();
    const outcomes = (f.outcomes_str || "")
      .split("\n").map((l) => l.trim()).filter(Boolean)
      .map((l) => {
        const [metric, value] = l.split("|").map((s) => (s || "").trim());
        return metric && value ? { metric, value } : null;
      }).filter(Boolean);
    const features = (f.features_str || "").split("\n").map((l) => l.trim()).filter(Boolean);
    const process = (f.process_str || "")
      .split("\n").map((l) => l.trim()).filter(Boolean)
      .map((l) => {
        const [step, desc] = l.split("|").map((s) => (s || "").trim());
        return step && desc ? { step, desc } : null;
      }).filter(Boolean);
    const splitTags = (s) => (s || "").split(",").map((t) => t.trim().toLowerCase()).filter(Boolean);
    onSave({
      ...(f.id ? { id: f.id } : {}),
      title: f.title,
      subtitle: f.subtitle || null,
      slug: f.slug || null,
      category_slug: f.category_slug,
      cover_image: f.cover_image,
      video_url: f.video_url || null,
      media_format: f.media_format || "landscape",
      year: f.year || null,
      client: f.client || null,
      tech_stack: (f.tech_stack_str || "").split(",").map((t) => t.trim()).filter(Boolean),
      external_url: f.external_url || null,
      description: f.description || null,
      gallery: (f.gallery_str || "").split("\n").map((s) => s.trim()).filter(Boolean),
      outcomes,
      challenge: f.challenge || null,
      solution: f.solution || null,
      features,
      process,
      timeline: f.timeline || null,
      markets: splitTags(f.markets_str),
      services: splitTags(f.services_str),
      industries: splitTags(f.industries_str),
      order: Number(f.order) || 100,
      published: !!f.published,
    });
  };

  const isPortrait = f.media_format === "portrait";
  return (
    <Modal onCancel={onCancel} title={f.id ? "Edit portfolio item" : "Add portfolio item"} testid="portfolio-item-editor">
      <form onSubmit={submit} className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Title *" required value={f.title} onChange={(v) => setF({ ...f, title: v })} testid="pif-title" />
          <div>
            <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Category *</label>
            <select
              value={f.category_slug || ""}
              onChange={(e) => setCategory(e.target.value)}
              className="mt-2 w-full bg-background border border-border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
              data-testid="pif-category"
            >
              <option value="" disabled>Select category</option>
              {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
          </div>
        </div>

        <Field label="URL slug (auto-generated if blank)" value={f.slug || ""} onChange={(v) => setF({ ...f, slug: v })} testid="pif-slug" placeholder="whitmore-realty-concierge-portal" />
        <div className="text-[11px] text-muted-foreground -mt-2">
          Detail page will live at <code className="bg-muted px-1.5 py-0.5 rounded">/portfolio/&lt;slug&gt;</code>. Leave blank to auto-generate from the title.
        </div>

        {/* Media format */}
        <div>
          <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Media format *</label>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setF({ ...f, media_format: "landscape" })} data-testid="pif-format-landscape"
              className={`text-left rounded-2xl border p-4 transition-colors ${f.media_format === "landscape" ? "border-primary bg-primary/5 ring-2 ring-primary/30" : "border-border hover:border-primary/40"}`}>
              <div className="flex items-center gap-3">
                <div className="w-14 h-9 rounded-md border-2 border-current opacity-70" />
                <div>
                  <div className="font-heading font-semibold text-sm">Wide screen</div>
                  <div className="text-[11px] text-muted-foreground">16:9 · YouTube-style</div>
                </div>
              </div>
            </button>
            <button type="button" onClick={() => setF({ ...f, media_format: "portrait" })} data-testid="pif-format-portrait"
              className={`text-left rounded-2xl border p-4 transition-colors ${f.media_format === "portrait" ? "border-primary bg-primary/5 ring-2 ring-primary/30" : "border-border hover:border-primary/40"}`}>
              <div className="flex items-center gap-3">
                <div className="w-6 h-10 rounded-md border-2 border-current opacity-70" />
                <div>
                  <div className="font-heading font-semibold text-sm">Reel / Mobile</div>
                  <div className="text-[11px] text-muted-foreground">9:16 · shown in a phone frame</div>
                </div>
              </div>
            </button>
          </div>
        </div>

        <FieldArea label="Subtitle / one-liner" value={f.subtitle || ""} onChange={(v) => setF({ ...f, subtitle: v })} rows={2} testid="pif-subtitle" />
        <ImageUploader
          label={isPortrait ? "Poster / mobile screenshot *" : "Cover image *"}
          hint={isPortrait
            ? "Recommended 1080 × 1920 px (9:16 portrait). Rendered inside a realistic phone frame."
            : (f.video_url ? "Used as the video poster before play. Recommended 1200 × 900 px (4:3)." : "Recommended 1200 × 900 px (4:3). JPG/PNG/WEBP, auto-optimized.")}
          value={f.cover_image || ""}
          onChange={(v) => setF({ ...f, cover_image: v })}
          testid="pif-cover"
          maxW={isPortrait ? 1080 : 1600}
          maxH={isPortrait ? 1920 : 1200}
          aspect={isPortrait ? "9 / 16" : "4 / 3"}
        />
        <Field
          label={isPortrait ? "Video URL (optional) — Bunny Stream / YouTube Shorts / IG Reel iframe" : "Video URL (optional) — Bunny Stream / YouTube / Vimeo iframe"}
          value={f.video_url || ""}
          onChange={(v) => setF({ ...f, video_url: v })}
          testid="pif-video"
          placeholder="https://iframe.mediadelivery.net/embed/…"
        />
        <div className="text-[11px] text-muted-foreground -mt-2">
          If set, the detail page shows a Play button that opens a full-screen player.
        </div>

        <FieldArea label="Description (long-form, shown on the detail page)" value={f.description || ""} onChange={(v) => setF({ ...f, description: v })} rows={5} testid="pif-description" placeholder="Tell the story: what we built, how, and what changed for the client." />

        <FieldArea
          label="Gallery images (one image URL per line — shown as a scroll-through gallery on the detail page)"
          value={f.gallery_str}
          onChange={(v) => setF({ ...f, gallery_str: v })}
          rows={5}
          testid="pif-gallery"
          placeholder={"https://…/shot-1.jpg\nhttps://…/shot-2.jpg"}
        />

        <FieldArea
          label={"Outcomes (one per line, format: metric | value)"}
          value={f.outcomes_str}
          onChange={(v) => setF({ ...f, outcomes_str: v })}
          rows={4}
          testid="pif-outcomes"
          placeholder={"Qualified viewings | +312%\nCSAT | 98%"}
        />

        {/* ---- Standardised case-study fields (Challenge / Solution / Features / Process / Timeline) ---- */}
        <div className="rounded-2xl border border-dashed border-primary/30 bg-primary/5 p-4">
          <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-primary mb-3">Case study · verified-only fields</div>
          <div className="space-y-4">
            <FieldArea
              label="Challenge (the business problem)"
              value={f.challenge || ""}
              onChange={(v) => setF({ ...f, challenge: v })}
              rows={3}
              testid="pif-challenge"
              placeholder="What was broken? What did the client need to solve?"
            />
            <FieldArea
              label="Solution (what we built and why)"
              value={f.solution || ""}
              onChange={(v) => setF({ ...f, solution: v })}
              rows={3}
              testid="pif-solution"
              placeholder="How did we approach it? What was the delivered outcome?"
            />
            <FieldArea
              label="Features (one per line — shipped features / capabilities)"
              value={f.features_str}
              onChange={(v) => setF({ ...f, features_str: v })}
              rows={4}
              testid="pif-features"
              placeholder={"AI voice qualification\nCalendar booking\nCRM handoff"}
            />
            <FieldArea
              label="Process (one step per line, format: step | description)"
              value={f.process_str}
              onChange={(v) => setF({ ...f, process_str: v })}
              rows={4}
              testid="pif-process"
              placeholder={"Discover | Two-week paid discovery mapping every workflow\nDesign | Clickable prototype signed off by stakeholders"}
            />
            <Field
              label="Timeline"
              value={f.timeline || ""}
              onChange={(v) => setF({ ...f, timeline: v })}
              testid="pif-timeline"
              placeholder="12 weeks · Q1 2025"
            />
          </div>
        </div>

        {/* ---- Cross-relationship tags — drive Related Case Studies on market/service/industry pages ---- */}
        <div className="rounded-2xl border border-dashed border-primary/30 bg-primary/5 p-4">
          <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-primary mb-3">Relationships · powers cross-linking</div>
          <div className="grid md:grid-cols-3 gap-4">
            <Field
              label="Markets (comma-separated slugs)"
              value={f.markets_str || ""}
              onChange={(v) => setF({ ...f, markets_str: v })}
              testid="pif-markets"
              placeholder="uk, pk, us, ae, sa, de, europe"
            />
            <Field
              label="Services (comma-separated slugs)"
              value={f.services_str || ""}
              onChange={(v) => setF({ ...f, services_str: v })}
              testid="pif-services"
              placeholder="crm-development, ai-data-science"
            />
            <Field
              label="Industries (comma-separated slugs)"
              value={f.industries_str || ""}
              onChange={(v) => setF({ ...f, industries_str: v })}
              testid="pif-industries"
              placeholder="education, healthcare, retail"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Year" value={f.year || ""} onChange={(v) => setF({ ...f, year: v })} testid="pif-year" placeholder="2025" />
          <Field label="Client" value={f.client || ""} onChange={(v) => setF({ ...f, client: v })} testid="pif-client" placeholder="Whitmore Realty" />
          <Field label="Sort order" type="number" value={String(f.order)} onChange={(v) => setF({ ...f, order: v })} testid="pif-order" />
        </div>
        <Field label="Tech stack (comma-separated)" value={f.tech_stack_str} onChange={(v) => setF({ ...f, tech_stack_str: v })} testid="pif-tech" placeholder="React, FastAPI, MongoDB" />
        <Field label="External URL (optional)" value={f.external_url || ""} onChange={(v) => setF({ ...f, external_url: v })} testid="pif-url" placeholder="https://…  or  /projects/…" />
        <label className="flex items-center gap-2"><input type="checkbox" checked={!!f.published} onChange={(e) => setF({ ...f, published: e.target.checked })} data-testid="pif-published" /> <span className="text-sm">Published</span></label>
        <div className="flex gap-3 pt-2">
          <button type="submit" className="rounded-full px-5 py-2.5 bg-primary text-primary-foreground text-sm font-medium inline-flex items-center gap-2" data-testid="pif-submit"><Save className="w-4 h-4" /> Save</button>
          <button type="button" onClick={onCancel} className="rounded-full px-5 py-2.5 border border-border text-sm">Cancel</button>
        </div>
      </form>
    </Modal>
  );
}
